// src/config/stationData.js
// 单站控制台演示数据（后端未接入，供界面评审）
// 数据口径参照《云平台单站控制（削峰填谷）需求文档》《云平台单站控制（光储充）需求文档》

// ============================== 站点台账 ==============================
export const STATIONS = [
  {
    id: 'hn',
    name: '海宁工业园站',
    type: 'pvc',
    typeLabel: '光储充一体站',
    location: '浙江省嘉兴市海宁市经济开发区',
    onlineAt: '2024-06-18',
    age: 480,
    rated: { power: '1.25 MW', capacity: '2.50 MWh' },
    grid: { voltage: '10 kV', transformer: '10/0.4 kV · 2×1250 kVA' },
    has: { pv: true, charger: true },
    comm: 'normal',
    strategyOn: true,
  },
  {
    id: 'yx',
    name: '余新镇储能站',
    type: 'peak',
    typeLabel: '削峰填谷储能站',
    location: '浙江省嘉兴市南湖区余新镇',
    onlineAt: '2023-11-02',
    age: 709,
    rated: { power: '0.50 MW', capacity: '1.00 MWh' },
    grid: { voltage: '10 kV', transformer: '10/0.4 kV · 1×800 kVA' },
    has: { pv: false, charger: false },
    comm: 'normal',
    strategyOn: true,
  },
  {
    id: 'nx',
    name: '南湖充电站',
    type: 'pvc',
    typeLabel: '光储充一体站',
    location: '浙江省嘉兴市南湖区科技城',
    onlineAt: '2025-03-09',
    age: 216,
    rated: { power: '0.80 MW', capacity: '1.60 MWh' },
    grid: { voltage: '10 kV', transformer: '10/0.4 kV · 1×1000 kVA' },
    has: { pv: true, charger: true },
    comm: 'delay',
    strategyOn: false,
  },
  {
    // 非削峰填谷 / 非光储充 → 走《云平台（能源管家）需求文档》2.7 单站监控界面（评审 66）
    id: 'xz',
    name: '秀洲经开区储能站',
    type: 'ems',
    typeLabel: '储能电站',
    location: '浙江省嘉兴市秀洲区经济技术开发区',
    onlineAt: '2024-04-15',
    age: 550,
    rated: { power: '0.90 MW', capacity: '1.80 MWh' },
    grid: { voltage: '10 kV', transformer: '10/0.4 kV · 1×1250 kVA' },
    has: { pv: false, charger: false },
    comm: 'normal',
    strategyOn: true,
  },
];

export const getStation = (id) => STATIONS.find((s) => s.id === id) || STATIONS[0];

/** 系统效率(%) = 储能表放电量 / 储能表充电量 × 100% */
export const systemEfficiencyPct = (charge, discharge) => (
  charge > 0 ? +((discharge / charge) * 100).toFixed(1) : 0
);

/** 为含 charge/discharge 的行补上 efficiency，保证与公式一致 */
export const withSystemEfficiency = (rows) => (rows || []).map((r) => ({
  ...r,
  efficiency: systemEfficiencyPct(r.charge, r.discharge),
}));

// ============================== 首页概览数据 ==============================
export const getOverview = (id) => {
  const base = { hn: 1, yx: 0.42, nx: 0.66 }[id] || 1;
  const st = getStation(id);
  const chargeTotal = 742.6 * base;
  const dischargeTotal = 642.1 * base;
  return {
    chargeToday: Math.round(2380 * base),
    dischargeToday: Math.round(2065 * base),
    chargeTotal: chargeTotal.toFixed(1),
    dischargeTotal: dischargeTotal.toFixed(1),
    incomeYesterday: Math.round(4180 * base),
    incomeTotal: Math.round(486300 * base),
    efficiency: systemEfficiencyPct(chargeTotal, dischargeTotal).toFixed(1),
    soc: Math.round(78 * (base > 0.8 ? 1 : 0.92)),
    availableEnergy: Math.round(1960 * base),
    storageState: '放电',
    storagePower: Math.round(218 * base),
    gridPower: -Math.round(96 * base),
    loadPower: Math.round(268 * base),
    pvPower: st.has.pv ? Math.round(146 * base) : 0,
  };
};

// ============================== 设备清单 ==============================
export const getDevices = (id) => {
  const st = getStation(id);
  const pcsCount = st.type === 'pvc' ? 3 : 2;
  const pcs = Array.from({ length: pcsCount }, (_, i) => ({
    id: `PCS-${String(i + 1).padStart(2, '0')}`,
    name: `PCS #${i + 1}`,
    running: i !== pcsCount - 1 || st.comm === 'normal',
    grid: i !== pcsCount - 1 ? '并网' : '离网',
    mode: i === 0 ? '本地' : '远程',
    p: Math.round((st.type === 'pvc' ? 250 : 250) * (0.82 - i * 0.06)),
    q: Math.round(18 - i * 3),
    pf: (0.98 - i * 0.01).toFixed(3),
    chargeToday: Math.round(820 - i * 60),
    dischargeToday: Math.round(760 - i * 55),
    moduleTemp: 38 + i * 2,
    envTemp: 27 + i,
    freq: (50 + (i === 0 ? 0.01 : -0.02)).toFixed(2),
    uabc: [`${398 + i}.${i}`, `${401 + i}.${i}`, `${400 + i}.${i}`],
    iabc: [`${182 - i * 9}.4`, `${180 - i * 9}.1`, `${181 - i * 9}.7`],
    branches: [
      { name: '支路1', p: Math.round(126 - i * 8), u: 712 + i * 3, i: Math.round(178 - i * 10) },
      { name: '支路2', p: Math.round(124 - i * 8), u: 710 + i * 3, i: Math.round(176 - i * 10) },
    ],
    alarm: i === pcsCount - 1 ? '直流侧绝缘偏低' : '',
  }));

  const racks = Array.from({ length: st.type === 'pvc' ? 6 : 4 }, (_, i) => ({
    id: `RACK-${String(i + 1).padStart(2, '0')}`,
    name: `电池堆 #${i + 1}`,
    state: i === 4 ? '静置' : '放电',
    // BMS 工作状态：未知 / 就绪 / 故障 / 维护 / 禁充 / 禁放 / 禁充放
    workState: i === 4 ? '故障' : i === 3 ? '禁充' : '就绪',
    contactorPos: '闭合',
    contactorNeg: '闭合',
    voltage: 712 + i * 4,
    current: Math.round(178 - i * 12),
    temp: 29 + (i % 3),
    soc: 78 - i * 2,
    soh: 98 - i * 0.4,
    available: Math.round(326 - i * 12),
    cells: 104,
    alarm: i === 4 ? '单体压差偏大' : '',
  }));

  const cells = Array.from({ length: 104 }, (_, i) => ({
    no: i + 1,
    voltage: +(3.331 + Math.sin(i / 7) * 0.012 - (i === 37 ? 0.055 : 0)).toFixed(3),
    temp: +(27.6 + Math.cos(i / 5) * 0.8 + (i === 61 ? 4.2 : 0)).toFixed(1),
    soc: 78,
    soh: 98,
  }));

  const meters = [
    { id: 'M-01', name: '并网柜电表', u: [230.4, 229.8, 231.1], i: [421.6, 419.2, 423.5], p: 289.4, q: 21.6, pf: 0.997, ep: 1284.6, state: '正常', freq: 50.01, wiring: '三相四线' },
    { id: 'M-02', name: '储能进线电表', u: [398.2, 397.6, 399.1], i: [214.8, 213.5, 215.6], p: 148.2, q: 9.4, pf: 0.998, ep: 962.3, state: '正常', freq: 50.0, wiring: '三相四线' },
    { id: 'M-03', name: '负荷出线电表', u: [398.6, 398.1, 398.9], i: [386.2, 384.7, 387.1], p: 268.7, q: 14.2, pf: 0.995, ep: 2140.8, state: '正常', freq: 49.98, wiring: '三相四线' },
    ...(st.has.pv ? [{ id: 'M-04', name: '光伏并网电表', u: [398.4, 398.2, 398.8], i: [212.4, 211.8, 213.2], p: 146.3, q: 0.8, pf: 1.0, ep: 486.2, state: '正常', freq: 50.02, wiring: '三相三线' }] : []),
  ];

  const hvac = Array.from({ length: st.type === 'pvc' ? 4 : 3 }, (_, i) => ({
    id: `AC-${String(i + 1).padStart(2, '0')}`,
    name: `空调 #${i + 1}`,
    running: i !== 2,
    mode: ['制冷', '制冷', '除湿', '送风'][i % 4],
    setTemp: 26,
    returnTemp: 27 + (i % 2),
    supplyTemp: +(19 + i * 0.5).toFixed(1),   // 出风温度
    humidity: +(52.6 + i * 1.3).toFixed(1),   // 环境湿度 %RH
    fan: i !== 2 ? '运行' : '停止',
    compressor: i === 0 ? '运行' : i === 1 ? '变频运行' : '停止',
    state: i === 2 ? '通信中断' : '正常',
    alarm: i === 1 ? '回风温度偏高' : '',
  }));

  return {
    pcs,
    racks,
    cells,
    meters,
    hvac,
    liquid: {
      id: 'LC-01',
      name: '液冷机组',
      running: true,
      mode: '自动',
      setTemp: 25,
      supplyTemp: 22.4,
      returnTemp: 26.7,
      pump: '运行',
      fan: '运行',
      pressure: 0.42,
      state: '正常',
    },
    pv: st.has.pv
      ? Array.from({ length: 3 }, (_, i) => ({
        id: `PV-${String(i + 1).padStart(2, '0')}`,
        name: `光伏逆变器 #${i + 1}`,
        running: true,
        dcPower: 52 - i * 4,
        acPower: 49 - i * 4,
        todayEnergy: 186 - i * 22,
        mppt: 4,
        dcVoltage: 620 + i * 8,
        efficiency: (97.4 - i * 0.3).toFixed(1),
        state: '正常',
      }))
      : [],
    charger: st.has.charger
      ? Array.from({ length: 6 }, (_, i) => ({
        id: `CP-${String(i + 1).padStart(2, '0')}`,
        name: `充电桩 #${i + 1}`,
        guns: 2,
        state: i === 5 ? '离线' : i % 3 === 0 ? '充电中' : '待机',
        power: i % 3 === 0 ? 118 - i * 3 : 0,
        todayEnergy: 142 - i * 11,
        collect: i % 2 === 0 ? '直连' : '电表采集',
      }))
      : [],
    // 消防安全：按 消防主机 / 温湿度计 / 水浸 / 烟感 分类（温湿度保留 0.1 ℃ / 0.1 %RH 精度）
    fire: [
      {
        id: 'FS-HOST-01', cat: '消防主机', name: '消防主机', state: '正常',
        rows: [['主机状态', '正常'], ['备电状态', '正常'], ['联动模式', '自动'], ['通信状态', '正常']],
      },
      {
        id: 'TH-01', cat: '温湿度计', name: '温湿度计 #1', state: '正常',
        temp: 24.6, humidity: 48.2,
        rows: [['温度', '24.6 ℃'], ['湿度', '48.2 %RH'], ['通信状态', '正常']],
      },
      {
        id: 'TH-02', cat: '温湿度计', name: '温湿度计 #2', state: '正常',
        temp: 25.3, humidity: 50.7,
        rows: [['温度', '25.3 ℃'], ['湿度', '50.7 %RH'], ['通信状态', '正常']],
      },
      {
        id: 'WL-01', cat: '水浸', name: '水浸探测器 #1', state: '正常',
        rows: [['漏水状态', '无漏水'], ['通信状态', '正常']],
      },
      {
        id: 'WL-02', cat: '水浸', name: '水浸探测器 #2', state: '告警',
        rows: [['漏水状态', '检测到积水'], ['通信状态', '正常']],
      },
      {
        id: 'SM-01', cat: '烟感', name: '烟感探测器 #1', state: '正常',
        rows: [['烟雾浓度', '0.02 %obs/m'], ['通信状态', '正常']],
      },
      {
        id: 'SM-02', cat: '烟感', name: '烟感探测器 #2', state: '正常',
        rows: [['烟雾浓度', '0.01 %obs/m'], ['通信状态', '正常']],
      },
      {
        id: 'SM-03', cat: '烟感', name: '烟感探测器 #3', state: '正常',
        rows: [['烟雾浓度', '0.00 %obs/m'], ['通信状态', '正常']],
      },
    ],
    // 光储充文档未列举 UPS / DCDC，故光储充站点不注入这两类设备；削峰填谷文档包含 DCDC 与 UPS
    others: st.type === 'pvc' ? [] : [
      { id: 'UPS-01', name: 'UPS', running: true, voltage: 229.6, battery: 96, load: 32, state: '正常' },
      ...(st.type === 'peak' ? [{ id: 'DCDC-01', name: 'DCDC 变换器', running: true, inVoltage: 710, outVoltage: 750, power: 46, state: '正常' }] : []),
    ],
  };
};

// ============================== 分时电价 ==============================
// 谷 / 平 / 峰 / 尖，单位 ¥/kWh
export const TARIFF = {
  valley: { label: '谷', price: 0.32, ranges: [[0, 6], [23, 24]] },
  flat: { label: '平', price: 0.65, ranges: [[6, 8], [11, 13], [22, 23]] },
  peak: { label: '峰', price: 1.05, ranges: [[8, 11], [13, 19]] },
  sharp: { label: '尖', price: 1.28, ranges: [[19, 22]] },
};

export const tariffAt = (hour) => {
  const list = [TARIFF.sharp, TARIFF.peak, TARIFF.flat, TARIFF.valley];
  for (const t of list) {
    if (t.ranges.some(([a, b]) => hour >= a && hour < b)) return t;
  }
  return TARIFF.flat;
};

// ============================== 预置策略模板（削峰填谷；须先于运行曲线造数） ==============================
export const getPresetStrategy = () => [
  { key: 1, start: '00:00', end: '06:00', power: 300, state: '充电', rule: '按可用PCS数量均分', pcs: '全部 PCS' },
  { key: 2, start: '06:00', end: '08:00', power: 0, state: '待机', rule: '—', pcs: '全部 PCS' },
  { key: 3, start: '08:00', end: '11:00', power: -250, state: '放电', rule: '按PCS数量均分', pcs: '全部 PCS' },
  { key: 4, start: '11:00', end: '13:00', power: 150, state: '充电', rule: '按可用PCS数量均分', pcs: 'PCS-01, PCS-02' },
  { key: 5, start: '13:00', end: '19:00', power: -220, state: '放电', rule: '按可用PCS数量均分', pcs: '全部 PCS' },
  { key: 6, start: '19:00', end: '22:00', power: -280, state: '放电', rule: '按PCS数量均分', pcs: '全部 PCS' },
  { key: 7, start: '22:00', end: '24:00', power: 260, state: '充电', rule: '按可用PCS数量均分', pcs: '全部 PCS' },
];

// ============================== 运行曲线（当日 0 点 – 次日 6 点 · 15 分钟）==============================
// 演示功率：分段常数 / 档位台阶（禁连续 sin）；储能充正放负

const demo01 = (seed) => {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return x - Math.floor(x);
};
const hhmmToMin = (s) => {
  const [h, m] = String(s).split(':').map(Number);
  return h * 60 + (m || 0);
};

export const strategyPowerAtMin = (minuteOfDay) => {
  const m = ((minuteOfDay % 1440) + 1440) % 1440;
  const row = getPresetStrategy().find(
    (r) => m >= hhmmToMin(r.start) && m < hhmmToMin(r.end),
  );
  return row ? row.power : 0;
};

export const loadPowerAtMin = (minuteOfDay, scale = 1) => {
  const m = ((minuteOfDay % 1440) + 1440) % 1440;
  let base = 170;
  if (m < 360) base = 150;
  else if (m < 480) base = 210;
  else if (m < 720) base = 360;
  else if (m < 780) base = 280;
  else if (m < 1080) base = 380;
  else if (m < 1260) base = 300;
  else if (m < 1320) base = 220;
  const slot = Math.floor(m / 45);
  const jump = demo01(slot * 17 + 3) > 0.72 ? Math.round((demo01(slot * 9) - 0.5) * 80) : 0;
  return Math.round((base + jump) * scale);
};

export const pvPowerAtMin = (minuteOfDay, peakKw, scale = 1) => {
  if (!peakKw) return 0;
  const m = ((minuteOfDay % 1440) + 1440) % 1440;
  const h = m / 60;
  if (h < 6.5 || h >= 18.5) return 0;
  const bin = Math.floor((h - 6.5) / 0.5);
  const mid = 12;
  const envelope = Math.max(0, 1 - Math.abs(bin - mid) / mid);
  let level = Math.round(envelope * 20) / 20;
  if (demo01(bin * 13 + 11) > 0.82) level = Math.max(0, level - 0.35);
  else if (demo01(bin * 7 + 5) > 0.88) level = Math.max(0, level - 0.15);
  return Math.round(peakKw * 1.05 * level * scale);
};

export const getPowerCurve = (id) => {
  const ov = getOverview(id);
  const out = [];
  let soc = 42;
  const pvPeak = id === 'yx' ? 0 : (ov.pvPower || 0);
  // 文档要求：X 轴为「今日 0 点 ~ 明日 6 点」，共 30 小时；15 分钟颗粒度 → 120 点
  // 跨日段使用「次日 HH:MM」标签，保证 X 轴类别唯一（不与当日 00:00 冲突）
  for (let i = 0; i < 30 * 4; i += 1) {
    const hour = i * 0.25;      // 0 ~ 29.75
    const hod = hour % 24;      // 当日时刻，用于电价与曲线形态
    const minuteOfDay = Math.round(hod * 60);
    const t = tariffAt(Math.floor(hod));
    const mm = String((i % 4) * 15).padStart(2, '0');
    const clock = hour >= 24
      ? `次日${String(Math.floor(hour) - 24).padStart(2, '0')}:${mm}`
      : `${String(Math.floor(hour)).padStart(2, '0')}:${mm}`;
    const pv = pvPowerAtMin(minuteOfDay, pvPeak);
    const load = loadPowerAtMin(minuteOfDay);
    const plan = strategyPowerAtMin(minuteOfDay);
    let storage = plan;
    if (pv > 80 && t.label === '峰' && plan < 0) storage = Math.max(plan, -120);
    soc = Math.max(12, Math.min(96, soc + storage / 620));
    out.push({
      clock,
      tariff: t.label,
      price: t.price,
      pv,
      load,
      storage,
      grid: Math.round(load + storage - pv),
      soc: +soc.toFixed(1),
      strategy: plan,
      demandLimit: 420,
      reverseLimit: 20, // 逆功率限制值（功率下限），文档要求与需量限制一同用于分析比较
    });
  }
  return out;
};

// PCS 曲线（近 2 天对比 · 小时级设定台阶）
export const getPcsCurve = () => {
  const dcSteps = [260, 260, 260, 260, 260, 260, 0, 0, -220, -220, -220, 140, 140, -200, -200, -200, -200, -200, -200, -250, -250, -250, 240, 240];
  const dcYest = [250, 250, 250, 250, 250, 250, 0, 0, -200, -200, -210, 120, 120, -180, -180, -190, -190, -190, -190, -230, -230, -230, 220, 220];
  return Array.from({ length: 24 }, (_, h) => {
    const dc = dcSteps[h];
    const mag = Math.abs(dc);
    return {
      clock: `${String(h).padStart(2, '0')}:00`,
      today: +(32 + (h >= 8 && h < 20 ? 8 : 0) + (h % 3 === 0 ? 1.5 : 0)).toFixed(1),
      yesterday: +(31 + (h >= 8 && h < 20 ? 7 : 0) + (h % 4 === 0 ? 1.2 : 0)).toFixed(1),
      aVoltage: +(398 + (h >= 8 && h < 22 ? 1.2 : 0) + (h % 6) * 0.15).toFixed(1),
      aCurrent: +(mag > 0 ? 40 + mag * 0.55 : 8 + (h % 2) * 2).toFixed(1),
      dcPower: dc,
      dcPowerYesterday: dcYest[h],
    };
  });
};

// 电池堆 / 簇曲线（近 7 天）
export const getRackCurve = () =>
  Array.from({ length: 7 }, (_, i) => ({
    clock: `${i + 4}日`,
    soc: +(72 + Math.sin(i) * 9).toFixed(1),
    voltage: +(704 + Math.cos(i) * 12).toFixed(1),
    current: +(160 + Math.sin(i / 1.6) * 34).toFixed(1),
    tempMax: +(29 + (i % 3)).toFixed(1),
    tempMin: +(24 + (i % 2)).toFixed(1),
  }));

// 单体曲线（近 7 天，取 1/26/52/78/104 号代表点）
export const getCellCurve = () => {
  const picks = [1, 26, 52, 78, 104];
  return Array.from({ length: 7 }, (_, d) => {
    const row = { clock: `${d + 4}日` };
    picks.forEach((n) => { row[`c${n}`] = +(3.328 + Math.sin((d + n) / 6) * 0.01).toFixed(3); });
    return row;
  });
};

// ============================== 统计报表 ==============================
export const getEnergyReport = () => [
  { key: 1, day: '10-05', charge: 2180, discharge: 1890, pv: 1240, load: 6420, grid: 7350 },
  { key: 2, day: '10-06', charge: 2460, discharge: 2130, pv: 1420, load: 6890, grid: 7920 },
  { key: 3, day: '10-07', charge: 2310, discharge: 2020, pv: 1180, load: 6540, grid: 7670 },
  { key: 4, day: '10-08', charge: 2380, discharge: 2065, pv: 1310, load: 6710, grid: 7780 },
  { key: 5, day: '10-09', charge: 2520, discharge: 2180, pv: 1460, load: 7020, grid: 8080 },
];

export const getIncomeReport = () => [
  { key: 1, day: '10-05', valleyCharge: 698, peakDischarge: 1985, pvIncome: 806, total: 2093 },
  { key: 2, day: '10-06', valleyCharge: 787, peakDischarge: 2237, pvIncome: 923, total: 2373 },
  { key: 3, day: '10-07', valleyCharge: 739, peakDischarge: 2121, pvIncome: 767, total: 2149 },
  { key: 4, day: '10-08', valleyCharge: 762, peakDischarge: 2168, pvIncome: 852, total: 2258 },
  { key: 5, day: '10-09', valleyCharge: 806, peakDischarge: 2289, pvIncome: 949, total: 2432 },
];

// 电表报表（近 5 日 · 总进线表计计量）
export const getMeterReport = () => [
  { key: 1, day: '10-05', meter: '总进线', fp: 1680, ff: 2140, fv: 980, reverse: 0, reactive: 322 },
  { key: 2, day: '10-06', meter: '总进线', fp: 1740, ff: 2210, fv: 1020, reverse: 0, reactive: 335 },
  { key: 3, day: '10-07', meter: '总进线', fp: 1690, ff: 2160, fv: 990, reverse: 0, reactive: 318 },
  { key: 4, day: '10-08', meter: '总进线', fp: 1750, ff: 2230, fv: 1040, reverse: 0, reactive: 341 },
  { key: 5, day: '10-09', meter: '总进线', fp: 1810, ff: 2290, fv: 1080, reverse: 0, reactive: 356 },
];

// 电池簇曲线（近 7 天 · 三簇 SOC 对比）
export const getClusterCurve = () =>
  Array.from({ length: 7 }, (_, i) => ({
    clock: `${i + 4}日`,
    cluster1: +(70 + Math.sin(i) * 6).toFixed(1),
    cluster2: +(72 + Math.sin(i + 1) * 7).toFixed(1),
    cluster3: +(71 + Math.sin(i + 2) * 5).toFixed(1),
  }));

// 电池温度报表（按簇 · 最新采样）
export const getClusterTempReport = () => [
  { key: 1, cluster: '1 号簇', max: 31.2, min: 24.1, avg: 26.8, maxCell: '26 号', minCell: '12 号', time: '2026-10-09 06:00' },
  { key: 2, cluster: '2 号簇', max: 30.6, min: 23.8, avg: 26.4, maxCell: '78 号', minCell: '53 号', time: '2026-10-09 06:00' },
  { key: 3, cluster: '3 号簇', max: 31.0, min: 24.3, avg: 27.0, maxCell: '104 号', minCell: '88 号', time: '2026-10-09 06:00' },
];

// 电池电压报表（按簇 · 最新采样）
export const getClusterVoltageReport = () => [
  { key: 1, cluster: '1 号簇', max: 3.351, min: 3.312, avg: 3.328, diff: 0.039, time: '2026-10-09 06:00' },
  { key: 2, cluster: '2 号簇', max: 3.344, min: 3.305, avg: 3.321, diff: 0.039, time: '2026-10-09 06:00' },
  { key: 3, cluster: '3 号簇', max: 3.357, min: 3.318, avg: 3.333, diff: 0.039, time: '2026-10-09 06:00' },
];

// 导出列表（最近导出记录）
export const getExportList = () => [
  { key: 1, name: '电量报表_20261009.xlsx', type: '电量报表', time: '2026-10-09 16:20', state: '已完成', size: '18 KB' },
  { key: 2, name: '收益报表_20261008.xlsx', type: '收益报表', time: '2026-10-08 15:40', state: '已完成', size: '16 KB' },
  { key: 3, name: '电池电压报表_20261007.xlsx', type: '电池电压报表', time: '2026-10-07 11:05', state: '已完成', size: '22 KB' },
];

// ============================== 控制中心 ==============================
export const getStrategyCurve = () =>
  Array.from({ length: 48 }, (_, i) => {
    const hour = i / 2;
    const row = getPresetStrategy().find(
      (r) => hour >= Number(r.start.slice(0, 2)) + Number(r.start.slice(3)) / 60
        && hour < Number(r.end.slice(0, 2)) + Number(r.end.slice(3)) / 60,
    );
    return { clock: `${String(Math.floor(hour)).padStart(2, '0')}:${i % 2 ? '30' : '00'}`, power: row ? row.power : 0 };
  });

// 日前策略（固定值模式）
export const getDayAhead = () => ({
  mode: '固定值',
  chargePrice: 0.35,
  dischargePrice: 0.98,
  idleAction: '待机',
  todayPrice: '谷 0.32 / 平 0.65 / 峰 1.05 / 尖 1.28',
  plan: Array.from({ length: 96 }, (_, i) => {
    const hour = i / 4;
    const minuteOfDay = Math.round((hour % 24) * 60);
    const t = tariffAt(Math.floor((hour + 24) % 24));
    return {
      clock: `${String(Math.floor(hour)).padStart(2, '0')}:${String((i % 4) * 15).padStart(2, '0')}`,
      power: strategyPowerAtMin(minuteOfDay),
      price: t.price,
    };
  }),
});

// 保护参数
export const getProtection = () => ({
  battery: {
    socHigh: 95, socLow: 12, cellTempHigh: 55, cellTempLow: -10,
    cellVoltageHigh: 3.65, cellVoltageLow: 2.8, lowPowerAction: '停止放电并转充电',
  },
  demand: { enabled: true, limit: 420, action: '降低储能充电功率', fallback: '转静置' },
  reverse: { enabled: true, limit: 20, action: '降低放电功率', fallback: '转静置' },
  powerFactor: { enabled: true, target: 0.98, action: '调节无功输出' },
  pvRegulate: { enabled: true, limitPower: 380, ramp: 10, action: '限功率运行' },
});

// 设备控制动作（按文档：策略运行中下发会冲突，须先停策略）
export const getControlActions = (station) => [
  { group: 'PCS', items: ['开机', '关机', '并网', '离网', '故障复归'] },
  { group: 'BMS', items: ['主负接触器合闸（上电：先合闸再开 PCS）', '主负接触器分闸（下电：先关 PCS 再分闸）', '故障复归'] },
  { group: '空调', items: ['开机', '关机', '参数配置'] },
  { group: '液冷机组', items: ['开机', '关机', '参数配置'] },
  ...(station.has.pv ? [{ group: '光伏逆变器', items: ['开机', '关机', '限功率配置'] }] : []),
];

// 智能策略参数（参照《云平台（能源管家）需求文档》2.7.6.2 智能策略）
export const getSmartStrategy = () => ({
  params: {
    mode: '经济性运行',
    tariffType: '分时电价（尖峰平谷）',
    contract: '最大需量合同',
    transformerCapacity: 1250, // kVA
    storageCapacity: 2500,     // kWh
    socHigh: 90,
    socLow: 10,
  },
  // 逆功率保护：限制值保护总表功率不低于此值；目标值略高于限制值 1~5kW
  reverse: { limit: 20, target: 25, degree: '全站（总表）' },
  // 需量控制：限制值保护总表功率不高于此值；目标值略低于限制值 1~5kW
  demand: { limit: 420, target: 415, degree: '全站（总表）' },
});

// 设备读写点位（参照《云平台单站控制（削峰填谷）需求文档》【设备读写】· 需二次口令）
const WRITE_POINTS = {
  PCS: [
    { name: '有功功率设定', current: 250, unit: 'kW', range: '0~500' },
    { name: '无功功率设定', current: 18, unit: 'kVar', range: '-100~100' },
    { name: '功率因数设定', current: 0.98, unit: '—', range: '0~1' },
    { name: '直流过压保护', current: 780, unit: 'V', range: '600~850' },
    { name: '交流过压保护', current: 450, unit: 'V', range: '380~480' },
  ],
  BMS: [
    { name: '充电截止电压', current: 3.65, unit: 'V', range: '3.40~3.70' },
    { name: '放电截止电压', current: 2.8, unit: 'V', range: '2.60~3.00' },
    { name: '最大充电电流', current: 200, unit: 'A', range: '0~250' },
    { name: '最大放电电流', current: 200, unit: 'A', range: '0~250' },
    { name: '均充使能', current: '1', unit: '—', range: '0 / 1' },
  ],
  空调: [
    { name: '工作模式', current: '制冷', unit: '—', range: '制冷/制热/自动/自循环' },
    { name: '目标温度', current: 26, unit: '℃', range: '16~30' },
    { name: '风机档位', current: '自动', unit: '—', range: '低/中/高/自动' },
  ],
  液冷机组: [
    { name: '工作模式', current: '自动', unit: '—', range: '制冷/制热/自动/自循环' },
    { name: '目标温度', current: 25, unit: '℃', range: '15~30' },
    { name: '出水温度', current: 22, unit: '℃', range: '10~30' },
  ],
  光伏逆变器: [
    { name: '有功限值', current: 380, unit: 'kW', range: '0~500' },
    { name: '功率变化率', current: 10, unit: '%/min', range: '0~100' },
    { name: '无功设定', current: 0, unit: 'kVar', range: '-50~50' },
  ],
};

export const getWritePoints = (group) =>
  (WRITE_POINTS[group] || []).map((p) => ({ ...p, target: p.current }));

// 告警（今日 / 历史）
export const getAlarms = () => ({
  today: [
    { key: 1, level: '严重', title: '电芯温度过高', device: 'BMS RACK-05', value: '58 ℃', state: '待处理', time: '09:42:11' },
    { key: 2, level: '警告', title: '直流侧绝缘阻抗低', device: 'PCS-03', value: '220 kΩ', state: '待处理', time: '09:15:03' },
    { key: 3, level: '警告', title: '单体压差偏大', device: 'RACK-05 单体 38', value: '68 mV', state: '已确认', time: '08:51:26' },
    { key: 4, level: '提示', title: '空调通信中断', device: 'AC-03', value: '-', state: '待处理', time: '08:36:47' },
  ],
  history: [
    { key: 1, level: '警告', title: 'SOC 低于设定下限', device: 'BMS RACK-02', value: '11 %', state: '已恢复', time: '2026-10-08 21:04:33' },
    { key: 2, level: '提示', title: '光伏限功率运行', device: 'PV-02', value: '限 80 %', state: '已恢复', time: '2026-10-08 13:22:09' },
    { key: 3, level: '警告', title: '液冷出水温度偏高', device: 'LC-01', value: '32 ℃', state: '已恢复', time: '2026-10-07 15:41:52' },
    { key: 4, level: '严重', title: '需量越限', device: '并网柜电表 M-01', value: '463 kW', state: '已恢复', time: '2026-10-06 10:12:40' },
  ],
});

// ============================== 跨模块通用演示数据 ==============================
// 供「账号权限 / 分析中心 / 运维 / 运营 / 后台管理 / 客户中心 / 单站系统管理」页面骨架演示。
// 后端未接入，仅用于界面评审。

// ---------- 组织架构（部门树） ----------
export const DEPARTMENTS = [
  {
    key: 'd-1', title: '总部', children: [
      { key: 'd-1-1', title: '运维部' },
      { key: 'd-1-2', title: '运营部' },
      { key: 'd-1-3', title: '技术部' },
    ],
  },
  {
    key: 'd-2', title: '嘉兴分公司', children: [
      { key: 'd-2-1', title: '工程部' },
      { key: 'd-2-2', title: '市场部' },
    ],
  },
];

// ---------- 组织成员 ----------
export const MEMBERS = [
  { key: 1, name: '周翔宇', account: 'zzhouxy', dept: '技术部', role: '超级管理员', phone: '138****4270', stations: '全部站点', state: '启用' },
  { key: 2, name: '李文杰', account: 'liwj', dept: '运维部', role: '运维工程师', phone: '139****8812', stations: '海宁工业园站 / 余新镇储能站', state: '启用' },
  { key: 3, name: '陈思远', account: 'chensy', dept: '运营部', role: '运营专员', phone: '137****2205', stations: '全部站点', state: '启用' },
  { key: 4, name: '王梦琪', account: 'wangmq', dept: '工程部', role: '只读用户', phone: '135****9931', stations: '南湖充电站', state: '停用' },
  { key: 5, name: '赵一鸣', account: 'zhaoym', dept: '市场部', role: '只读用户', phone: '186****4477', stations: '全部站点', state: '启用' },
];

// ---------- 功能角色 ----------
export const ROLES = [
  { key: 1, name: '超级管理员', code: 'SUPER_ADMIN', members: 1, scope: '全部数据', builtin: '内置', desc: '拥有平台全部权限' },
  { key: 2, name: '运维工程师', code: 'OPS_ENGINEER', members: 1, scope: '所辖站点', builtin: '自定义', desc: '设备监控、控制与运维工单' },
  { key: 3, name: '运营专员', code: 'BIZ_STAFF', members: 1, scope: '全部数据', builtin: '自定义', desc: '运营分析与报表导出' },
  { key: 4, name: '只读用户', code: 'VIEWER', members: 2, scope: '指定站点', builtin: '内置', desc: '仅可查看，不可操作' },
];

// ---------- 权限树（角色配置用） ----------
export const PERM_TREE = [
  {
    key: 'p-display', title: '展示中心', children: [
      { key: 'p-display-screen', title: '数据大屏' },
      { key: 'p-display-multi', title: '多站概览' },
      { key: 'p-display-map', title: '站点地图' },
    ],
  },
  {
    key: 'p-monitor', title: '监控中心', children: [
      { key: 'p-monitor-realtime', title: '实时运行' },
      { key: 'p-monitor-station', title: '单站监控' },
      { key: 'p-monitor-control', title: '控制中心（需二次口令）' },
    ],
  },
  {
    key: 'p-alarm', title: '报警中心', children: [
      { key: 'p-alarm-fault', title: '故障报警' },
      { key: 'p-alarm-battery', title: '电池预警' },
    ],
  },
  {
    key: 'p-admin', title: '管理中心', children: [
      { key: 'p-admin-station', title: '站点管理' },
      { key: 'p-admin-member', title: '组织成员' },
      { key: 'p-admin-role', title: '角色管理' },
    ],
  },
];

// ---------- 邀请链接 ----------
export const INVITES = [
  { key: 1, code: 'INV-8F3K2A', role: '运维工程师', dept: '运维部', creator: '周翔宇', expire: '2026-11-11', used: '2 / 10', state: '生效中' },
  { key: 2, code: 'INV-2M9Q7Z', role: '只读用户', dept: '市场部', creator: '周翔宇', expire: '2026-10-25', used: '1 / 5', state: '生效中' },
  { key: 3, code: 'INV-5T1B8C', role: '运营专员', dept: '运营部', creator: '陈思远', expire: '2026-10-01', used: '5 / 5', state: '已用尽' },
];

// ---------- 外部公司 ----------
export const COMPANIES = [
  { key: 1, name: '嘉兴吉晟电力', type: '业主单位', contact: '周翔宇', phone: '138****4270', stations: 3, state: '合作中' },
  { key: 2, name: '杭州储能科技', type: '运维服务商', contact: '李文杰', phone: '139****8812', stations: 1, state: '合作中' },
  { key: 3, name: '浙江新能源集团', type: '投资方', contact: '赵一鸣', phone: '186****4477', stations: 0, state: '洽谈中' },
];

// ---------- 系统 / 操作日志 ----------
export const LOGS = [
  { key: 1, time: '2026-10-11 09:42:11', type: '操作日志', user: '周翔宇', action: '下发预置策略', target: '海宁工业园站', result: '成功', ip: '192.168.1.24' },
  { key: 2, time: '2026-10-11 09:15:03', type: '告警日志', user: '系统', action: '触发直流侧绝缘告警', target: 'PCS-03', result: '-', ip: '-' },
  { key: 3, time: '2026-10-11 08:51:26', type: '登录日志', user: '陈思远', action: '登录平台', target: '-', result: '成功', ip: '192.168.1.51' },
  { key: 4, time: '2026-10-11 08:36:47', type: '操作日志', user: '李文杰', action: '确认告警', target: 'AC-03 通信中断', result: '成功', ip: '192.168.1.33' },
  { key: 5, time: '2026-10-10 21:04:33', type: '登录日志', user: '王梦琪', action: '登录失败（密码错误）', target: '-', result: '失败', ip: '10.0.0.88' },
];

// ---------- 客户户号 ----------
export const CUSTOMERS = [
  { key: 1, no: '3304****2210', name: '海宁工业园站户号', company: '嘉兴吉晟电力', station: '海宁工业园站', tariff: '大工业用电', state: '正常' },
  { key: 2, no: '3304****3325', name: '余新镇储能站户号', company: '嘉兴吉晟电力', station: '余新镇储能站', tariff: '一般工商业', state: '正常' },
  { key: 3, no: '3304****4471', name: '南湖充电站户号', company: '嘉兴吉晟电力', station: '南湖充电站', tariff: '一般工商业', state: '正常' },
];

// ---------- 资产（doc01 3.8：在用资产 / 报废资产） ----------
export const ASSETS = [
  { key: 1, code: 'AS-PCS-0001', name: 'PCS 变流器', type: '储能设备', category: '储能设备', predictCfg: '已配置', station: '海宁工业园站', cabinet: '储能一体柜', ipc: 'IPC-01', vendor: '科华数据', model: 'KAC-250', factory: '2024-05-20', power: '250 kW', capacity: '—', operator: '周翔宇', state: '在役', customer: '海宁工业园', device: 'PCS-01', installDate: '2024-06-01', installPlace: 'PCS 舱 1 号位', history: [{ time: '2026-09-28 10:12', action: '配置预测值曲线' }] },
  { key: 2, code: 'AS-BAT-0001', name: '磷酸铁锂电池柜', type: '电池系统', category: '电池系统', predictCfg: '已配置', station: '海宁工业园站', cabinet: '电池柜', ipc: 'IPC-02', vendor: '宁德时代', model: 'CB-2.5MWh', factory: '2024-05-18', power: '—', capacity: '2.5 MWh', operator: '周翔宇', state: '在役', customer: '海宁工业园', device: '电池簇-01', installDate: '2024-06-01', installPlace: '电池舱 1 号位', history: [{ time: '2026-09-20 09:40', action: '关联客户与站点' }] },
  { key: 3, code: 'AS-PV-0001', name: '光伏逆变器', type: '光伏设备', category: '光伏设备', predictCfg: '未配置', station: '海宁工业园站', cabinet: '—', ipc: 'IPC-03', vendor: '华为', model: 'SUN2000-100K', factory: '2024-05-10', power: '100 kW', capacity: '—', operator: '李文杰', state: '在役', customer: '海宁工业园', device: 'PV-01', installDate: '2024-06-02', installPlace: '屋顶 1 区', history: [] },
  { key: 4, code: 'AS-CP-0001', name: '直流充电桩', type: '充电设备', category: '充电设备', predictCfg: '未配置', station: '南湖充电站', cabinet: '—', ipc: '—', vendor: '特来电', model: 'TCDZ-120', factory: '2025-02-26', power: '120 kW', capacity: '—', operator: '李文杰', state: '在役', customer: '南湖充电站', device: '充电桩-01', installDate: '2025-03-10', installPlace: '停车场 A 区', history: [] },
];

// 报废资产 tab
export const SCRAPPED_ASSETS = [
  { key: 1, code: 'AS-BAT-0000', name: '退役电池柜（样机）', station: '海宁工业园站', vendor: '宁德时代', model: 'CB-1.0MWh', factory: '2022-03-12', power: '—', capacity: '1.0 MWh', operator: '周翔宇', scrapAt: '2026-08-30', dest: '厂家回收' },
];

// ---------- 站点管理（doc01 3.1：所属站点 / 授权站点 + 标签管理） ----------
export const STATION_TAGS = [
  { key: 1, name: '光储充一体站', count: 2, color: 'blue' },
  { key: 2, name: '削峰填谷储能站', count: 1, color: 'green' },
  { key: 3, name: '重点保障站点', count: 1, color: 'orange' },
  { key: 4, name: '华东区域', count: 3, color: 'default' },
];

export const OWN_STATIONS = STATIONS.map((s, i) => ({
  key: s.id,
  name: s.name,
  typeLabel: s.typeLabel,
  location: s.location,
  power: s.rated.power,
  capacity: s.rated.capacity,
  onlineAt: s.onlineAt,
  visible: i === 2 ? '隐藏' : '显示',
  tags: i === 0 ? '光储充一体站 / 重点保障站点' : i === 1 ? '削峰填谷储能站 / 华东区域' : '光储充一体站 / 华东区域',
  comm: s.comm,
}));

export const AUTHORIZED_STATIONS = [
  { key: 'auth-1', name: '杭州临平储能站', typeLabel: '削峰填谷储能站', location: '浙江省杭州市临平区', power: '0.60 MW', capacity: '1.20 MWh', onlineAt: '2024-09-15', source: '浙江新能源集团', visible: '显示', tags: '工商业储能 / 华东区域' },
  { key: 'auth-2', name: '绍兴柯桥微网站', typeLabel: '光储充一体站', location: '浙江省绍兴市柯桥区', power: '0.40 MW', capacity: '0.80 MWh', onlineAt: '2025-01-20', source: '绍兴绿能科技', visible: '隐藏', tags: '光储充一体站' },
];

// ---------- 运维工单（doc01 2.15：4 种状态 tab + 类型/来源/期望处理时间） ----------
export const WORK_ORDERS = [
  { key: 1, no: 'WO-20261011-003', title: 'RACK-05 单体压差偏大检查', type: '故障检修', source: '故障转工单', level: '高', station: '海宁工业园站', assignee: '待分配', created: '2026-10-11 08:52', expect: '2026-10-12 09:00', finish: '', state: '待接单' },
  { key: 2, no: 'WO-20261012-005', title: '并网柜电表抄表异常核查', type: '故障检修', source: 'APP上报', level: '中', station: '余新镇储能站', assignee: '待分配', created: '2026-10-12 07:40', expect: '2026-10-12 18:00', finish: '', state: '待接单' },
  { key: 3, no: 'WO-20261011-004', title: 'PCS-03 直流侧绝缘偏低排查', type: '故障检修', source: '故障转工单', level: '紧急', station: '海宁工业园站', assignee: '李文杰', created: '2026-10-11 09:16', expect: '2026-10-11 14:00', finish: '', state: '待执行' },
  { key: 4, no: 'WO-20261010-006', title: '电池舱季度巡检', type: '巡视检查', source: '计划', level: '中', station: '全部站点', assignee: '赵一鸣', created: '2026-10-10 23:59', expect: '2026-10-15 09:00', finish: '', state: '待执行' },
  { key: 5, no: 'WO-20261010-002', title: 'AC-03 空调通信中断恢复', type: '故障检修', source: 'APP上报', level: '中', station: '海宁工业园站', assignee: '赵一鸣', created: '2026-10-10 16:20', expect: '2026-10-11 12:00', finish: '2026-10-10 18:40', state: '已完成' },
  { key: 6, no: 'WO-20261009-001', title: '液冷机组滤网清洗', type: '维护保养', source: '计划', level: '低', station: '余新镇储能站', assignee: '李文杰', created: '2026-10-09 10:05', expect: '2026-10-10 10:00', finish: '2026-10-09 15:30', state: '已完成' },
  { key: 7, no: 'WO-20261001-000', title: '光伏逆变器支路排查', type: '故障检修', source: '手动创建', level: '中', station: '南湖充电站', assignee: '李文杰', created: '2026-10-01 09:00', expect: '2026-10-02 18:00', finish: '2026-10-01 12:00', state: '已关闭' },
];

// ---------- 需求响应（doc01 2.19：今日响应 / 明日响应 / 响应记录） ----------
export const getTodayResponse = () => {
  const curve = Array.from({ length: 25 }, (_, i) => {
    const h = i;
    const inWindow = h >= 15 && h <= 17;
    const actual = h < 9 ? 0 : Math.round(180 + Math.sin((h - 6) / 4) * 120 + (h >= 9 && h <= 10 ? 280 : 0));
    return {
      clock: `${String(h).padStart(2, '0')}:00`,
      actual: Math.max(0, actual),
      target: inWindow ? 320 : 0,
      window: inWindow ? 1 : 0,
    };
  });
  return {
    dispatchNo: 'DR-20261012-01',
    issuedAt: '2026-10-12 09:30',
    targetPower: '320 kW',
    execTotal: '314 kW',
    dischargeToday: 1260,
    chargeToday: 420,
    curve,
  };
};

export const TOMORROW_EVENTS = [
  { key: 1, no: 'DR-20261013-01', mode: '削峰响应', issued: '2026-10-12 20:00', window: '2026-10-13 15:00-17:00', target: '300 kW', countdown: '18:24:10' },
  { key: 2, no: 'DR-20261013-02', mode: '填谷响应', issued: '2026-10-12 20:00', window: '2026-10-13 02:00-05:00', target: '250 kW', countdown: '05:24:10' },
];

export const RESPONSE_STAT = { times: 12, hours: 26.5, charge: 4260, discharge: 6820 };

export const RESPONSE_RECORDS = [
  { key: 1, no: 'DR-20260918', date: '2026-09-18', station: '海宁工业园站', mode: '削峰响应', window: '14:00-16:00', target: '300 kW', actual: '286 kW', rate: 95.3, subsidy: '¥ 4,290', state: '已结算' },
  { key: 2, no: 'DR-20260822', date: '2026-08-22', station: '余新镇储能站', mode: '填谷响应', window: '02:00-05:00', target: '250 kW', actual: '263 kW', rate: 105.2, subsidy: '¥ 3,160', state: '已结算' },
  { key: 3, no: 'DR-20260730', date: '2026-07-30', station: '南湖充电站', mode: '削峰响应', window: '15:00-17:00', target: '280 kW', actual: '268 kW', rate: 95.7, subsidy: '¥ 3,880', state: '已结算' },
  { key: 4, no: 'DR-20261012', date: '2026-10-12', station: '海宁工业园站', mode: '削峰响应', window: '15:00-17:00', target: '320 kW', actual: '—', rate: null, subsidy: '—', state: '响应中' },
];

// ---------- 响应管理（doc01 2.19：今日响应 / 明日响应 / 历史响应） ----------
// 今日响应：当前正在执行的响应指令概览（结果编号 / 指令下发时间 / 目标 / 执行 / 充放电量）
export const TODAY_RESPONSE_CMD = {
  no: 'DR2025092500001',
  issuedAt: '2025-09-25 09:45:22',
  targetPower: 1250,
  execPower: 1248,
  discharge: 48.6,
  charge: 18.4,
  status: '响应中',
  mode: '削峰响应',
};

// 云平台今日实时总有功功率曲线（00:00–24:00，25 个采样点）；响应时段以淡黄色区块标注
export const TODAY_RESPONSE_WINDOWS = [['08:00', '10:00'], ['14:00', '16:00']];

export const getTodayPowerCurve = () => {
  const points = [
    [0, 0], [1, -32], [2, -58], [3, -46], [4, -22], [5, -4], [6, 16], [7, 42],
    [8, 88], [9, 90], [10, 64], [11, 34], [12, 6], [13, -8], [14, 86], [15, 80],
    [16, 48], [17, 20], [18, 2], [19, -22], [20, -44], [21, -30], [22, -12], [23, -2], [24, 0],
  ];
  const target = [
    0, -30, -55, -44, -20, -2, 18, 44, 88, 88, 60, 32, 4, -6, 86, 80,
    50, 18, 0, -20, -42, -28, -10, 0, 0,
  ];
  return points.map(([h, v], i) => ({
    clock: `${String(h).padStart(2, '0')}:00`,
    actual: v,
    target: target[i],
  }));
};

// 电站分配：当前响应指令下各电站的执行功率 / 充放电量分配
export const RESPONSE_STATION_ALLOC = [
  { key: 'hn', station: '海宁工业园站', rated: 1000, soc: 60, execPower: 668, discharge: 28.6, charge: 0 },
  { key: 'yx', station: '余新镇储能站', rated: 1000, soc: 70, execPower: 580, discharge: 20.0, charge: 0 },
];

// 单站当日功率曲线（点击「查看」弹出）
export const getStationPowerCurve = (seed = 1) => {
  const base = seed === 'yx' ? 540 : 620;
  return Array.from({ length: 25 }, (_, h) => ({
    clock: `${String(h).padStart(2, '0')}:00`,
    actual: Math.round(base * Math.max(0, Math.sin((h - 4) / 3.4)) + (h >= 8 && h <= 10 ? 240 : 0) + (h >= 14 && h <= 16 ? 200 : 0)),
  }));
};

// 响应中指令：当前正在执行的响应指令列表
export const RESPONSE_ACTIVE_CMDS = [
  { key: 1, no: 'DR2025092500006', issuedAt: '2025-09-25 15:00:00', mode: '削峰响应', target: 1250, state: '响应中', duration: '3分12秒', discharge: 12.4, charge: 0 },
  { key: 2, no: 'DR2025092500007', issuedAt: '2025-09-25 15:00:00', mode: '削峰响应', target: 800, state: '响应中', duration: '3分10秒', discharge: 8.2, charge: 0 },
];

// 已响应指令：表头展示成功 / 失败次数
export const RESPONSE_COMMANDS = {
  success: 12,
  fail: 3,
  rows: [
    { key: 1, no: 'DR2025092500001', issuedAt: '17:16:34', mode: '削峰响应', stage: '响应结束', target: '-', state: '成功', duration: '-', discharge: '-', charge: '-' },
    { key: 2, no: 'DR2025092500002', issuedAt: '17:14:34', mode: '削峰响应', stage: '响应开始', target: '-', state: '失败', duration: '5分30秒', discharge: 28, charge: 0 },
    { key: 3, no: 'DR2025092500003', issuedAt: '17:12:34', mode: '削峰响应', stage: '响应开始', target: 1000, state: '成功', duration: '5分30秒', discharge: 28, charge: 0 },
    { key: 4, no: 'DR2025092500004', issuedAt: '17:11:34', mode: '削峰响应', stage: '响应开始', target: 1000, state: '失败', duration: '-', discharge: '-', charge: '-' },
    { key: 5, no: 'DR2025092500005', issuedAt: '17:11:34', mode: '削峰响应', stage: '响应结束', target: '-', state: '成功', duration: '-', discharge: '-', charge: '-' },
  ],
};

// 明日响应：预约响应时段 + 倒计时，倒计时结束自动进入响应模式
export const TOMORROW_RESPONSE = [
  { key: 1, no: 'DR202509280001', mode: '削峰响应', issuedAt: '2025-09-28 10:40', window: '2025-12-01 13:00-16:00', target: 100, countdown: '23:55:28' },
  { key: 2, no: 'DR202509280002', mode: '填谷响应', issuedAt: '2025-09-28 10:40', window: '2025-11-27 19:00-21:00', target: 100, countdown: '11:20:04' },
];

// 历史响应：统计分析（累计成功响应次数 / 时长 / 充放电量）
export const RESPONSE_HISTORY_STAT = { success: 68, duration: 2120, charge: 348.6, discharge: 168.3 };

// 历史响应记录（可按日期 / 电站筛选；单击详情查看指令明细）
export const RESPONSE_HISTORY = [
  { key: 1, no: 'DR2025092500001', issuedAt: '2025-09-25 17:16:34', mode: '削峰响应', startAt: '2025-09-25 17:12:34', endAt: '2025-09-25 17:38:34', duration: 26, target: 1000, reduce: 220, state: '成功', discharge: 28.6, charge: 0, station: '海宁工业园站' },
  { key: 2, no: 'DR2025091800002', issuedAt: '2025-09-18 14:05:00', mode: '填谷响应', startAt: '2025-09-18 14:10:00', endAt: '2025-09-18 16:20:00', duration: 25, target: 600, reduce: -180, state: '成功', discharge: 0, charge: 32.4, station: '余新镇储能站' },
  { key: 3, no: 'DR2025091200003', issuedAt: '2025-09-12 09:30:00', mode: '削峰响应', startAt: '2025-09-12 09:35:00', endAt: '2025-09-12 11:35:00', duration: 120, target: 1200, reduce: 260, state: '失败', discharge: 12.0, charge: 0, station: '南湖充电站' },
  { key: 4, no: 'DR2025090500004', issuedAt: '2025-09-05 15:00:00', mode: '削峰响应', startAt: '2025-09-05 15:05:00', endAt: '2025-09-05 17:05:00', duration: 120, target: 1000, reduce: 205, state: '成功', discharge: 41.2, charge: 0, station: '海宁工业园站' },
  { key: 5, no: 'DR2025082800005', issuedAt: '2025-08-28 02:00:00', mode: '填谷响应', startAt: '2025-08-28 02:05:00', endAt: '2025-08-28 05:00:00', duration: 175, target: 500, reduce: -150, state: '成功', discharge: 0, charge: 46.8, station: '余新镇储能站' },
  { key: 6, no: 'DR2025082000006', issuedAt: '2025-08-20 19:00:00', mode: '削峰响应', startAt: '2025-08-20 19:05:00', endAt: '2025-08-20 21:00:00', duration: 115, target: 800, reduce: 168, state: '成功', discharge: 36.5, charge: 0, station: '南湖充电站' },
];

// ---------- 核算管理（doc01 2.20：核算单 / 核算规则 / 电价模板） ----------
export const SETTLE_RECORDS = [
  { key: 1, no: 'ST-20261001-001', station: '海宁工业园站', rule: '海宁收益核算规则', mode: '电价打折', period: '2026-09', by: '自动', readResult: '成功', calcResult: '成功', amount: 48260, genAt: '2026-10-01 02:00', state: '已确认' },
  { key: 2, no: 'ST-20261001-002', station: '余新镇储能站', rule: '余新收益核算规则', mode: '比例分成', period: '2026-09', by: '自动', readResult: '成功', calcResult: '成功', amount: 21480, genAt: '2026-10-01 02:00', state: '已确认' },
  { key: 3, no: 'ST-20261001-003', station: '南湖充电站', rule: '南湖收益核算规则', mode: '电价打折', period: '2026-09', by: '手动', readResult: '失败', calcResult: '失败', amount: 0, genAt: '2026-10-01 09:12', state: '未确认', note: '电表采集异常，无法获取抄表示数' },
  { key: 4, no: 'ST-20260901-004', station: '海宁工业园站', rule: '海宁收益核算规则', mode: '电价打折', period: '2026-08', by: '自动', readResult: '成功', calcResult: '成功', amount: 46820, genAt: '2026-09-01 02:00', state: '已确认' },
];

export const SETTLE_RULES = [
  { key: 1, no: 'RULE-0003', name: '海宁收益核算规则', tariff: '海宁2026电价模板', mode: '电价打折', discount: '85%', by: '自动', state: '生效', updated: '2026-09-28' },
  { key: 2, no: 'RULE-0002', name: '余新收益核算规则', tariff: '余新2026电价模板', mode: '比例分成', discount: '分红 20%', by: '自动', state: '生效', updated: '2026-09-20' },
  { key: 3, no: 'RULE-0001', name: '南湖收益核算规则', tariff: '南湖2026电价模板', mode: '电价打折', discount: '90%', by: '手动', state: '失效', updated: '2026-08-15' },
];

export const TARIFF_TEMPLATES = [
  { key: 1, no: 'TP-0002', name: '海宁2026电价模板', months: 12, latest: '2026-09', updated: '2026-09-28' },
  { key: 2, no: 'TP-0001', name: '余新2026电价模板', months: 12, latest: '2026-09', updated: '2026-09-20' },
  { key: 3, no: 'TP-0003', name: '南湖2026电价模板', months: 8, latest: '2026-08', updated: '2026-08-15' },
];

// 近 12 月应收金额（不含当月）
export const SETTLE_MONTH_TREND = [
  { month: '2025-10', amount: 41200 }, { month: '2025-11', amount: 43800 },
  { month: '2025-12', amount: 47600 }, { month: '2026-01', amount: 45100 },
  { month: '2026-02', amount: 39800 }, { month: '2026-03', amount: 44200 },
  { month: '2026-04', amount: 42800 }, { month: '2026-05', amount: 46500 },
  { month: '2026-06', amount: 50100 }, { month: '2026-07', amount: 55140 },
  { month: '2026-08', amount: 49230 }, { month: '2026-09', amount: 52960 },
];

// ---------- 运维计划 ----------
export const OM_PLANS = [
  { key: 1, name: '季度电池健康巡检', cycle: '每季度', station: '全部站点', owner: '运维部', next: '2026-12-15', state: '执行中' },
  { key: 2, name: '月度消防系统检测', cycle: '每月', station: '全部站点', owner: '运维部', next: '2026-11-01', state: '执行中' },
  { key: 3, name: '半年度变压器预防性试验', cycle: '每半年', station: '全部站点', owner: '工程部', next: '2027-04-01', state: '待启动' },
];

// ---------- 需求响应事件 ----------
export const DEMAND_EVENTS = [
  { key: 1, no: 'DR-20260918', type: '削峰响应', date: '2026-09-18 14:00-16:00', target: '300 kW', actual: '286 kW', subsidy: '¥ 4,290', state: '已结算' },
  { key: 2, no: 'DR-20260822', type: '填谷响应', date: '2026-08-22 02:00-05:00', target: '250 kW', actual: '263 kW', subsidy: '¥ 3,160', state: '已结算' },
  { key: 3, no: 'DR-20261012', type: '削峰响应', date: '2026-10-12 15:00-17:00', target: '320 kW', actual: '—', subsidy: '—', state: '待申报' },
];

// ---------- 收益核算（结算单） ----------
export const SETTLEMENTS = [
  { key: 1, month: '2026-09', valleyCharge: 21840, peakDischarge: 19620, pvIncome: 8520, total: 52960, state: '已出账' },
  { key: 2, month: '2026-08', valleyCharge: 20460, peakDischarge: 18310, pvIncome: 8140, total: 49230, state: '已出账' },
  { key: 3, month: '2026-07', valleyCharge: 22680, peakDischarge: 20040, pvIncome: 9010, total: 55140, state: '已出账' },
];

// ---------- 视频监控通道 ----------
export const VIDEO_CHANNELS = [
  { key: 1, name: '海宁-进线柜', station: '海宁工业园站', ip: '192.168.10.11', state: '在线', resolution: '1080P' },
  { key: 2, name: '海宁-电池舱', station: '海宁工业园站', ip: '192.168.10.12', state: '在线', resolution: '1080P' },
  { key: 3, name: '海宁-充电区', station: '海宁工业园站', ip: '192.168.10.13', state: '在线', resolution: '1080P' },
  { key: 4, name: '余新-电池舱', station: '余新镇储能站', ip: '192.168.20.11', state: '离线', resolution: '720P' },
  { key: 5, name: '南湖-光伏区', station: '南湖充电站', ip: '192.168.30.11', state: '在线', resolution: '1080P' },
  { key: 6, name: '秀洲-电池舱', station: '秀洲经开区储能站', ip: '192.168.40.11', state: '在线', resolution: '1080P' },
];

// ---------- 单站视频监控通道（设备监控 · 视频监控 tab） ----------
const VIDEO_BY_STATION = {
  hn: [
    { key: 'hn-1', name: '进线柜', ip: '192.168.10.11', state: '在线', resolution: '1080P' },
    { key: 'hn-2', name: '电池舱', ip: '192.168.10.12', state: '在线', resolution: '1080P' },
    { key: 'hn-3', name: '充电区', ip: '192.168.10.13', state: '在线', resolution: '1080P' },
    { key: 'hn-4', name: 'PCS 舱', ip: '192.168.10.14', state: '在线', resolution: '1080P' },
  ],
  yx: [
    { key: 'yx-1', name: '电池舱', ip: '192.168.20.11', state: '离线', resolution: '720P' },
    { key: 'yx-2', name: '并网柜区', ip: '192.168.20.12', state: '在线', resolution: '1080P' },
  ],
  nx: [
    { key: 'nx-1', name: '光伏区', ip: '192.168.30.11', state: '在线', resolution: '1080P' },
    { key: 'nx-2', name: '充电区', ip: '192.168.30.12', state: '在线', resolution: '1080P' },
    { key: 'nx-3', name: '电池舱', ip: '192.168.30.13', state: '在线', resolution: '1080P' },
  ],
  xz: [
    { key: 'xz-1', name: '电池舱', ip: '192.168.40.11', state: '在线', resolution: '1080P' },
    { key: 'xz-2', name: '并网柜区', ip: '192.168.40.12', state: '在线', resolution: '1080P' },
    { key: 'xz-3', name: 'PCS 舱', ip: '192.168.40.13', state: '在线', resolution: '1080P' },
  ],
};

export const getStationVideo = (id) => VIDEO_BY_STATION[id] || [];

// ---------- 站点地图点位 ----------
export const MAP_POINTS = [
  { key: 1, name: '海宁工业园站', lng: '120.68', lat: '30.53', power: 1250, state: 'online', typeLabel: '光储充一体站' },
  { key: 2, name: '余新镇储能站', lng: '120.79', lat: '30.71', power: 500, state: 'online', typeLabel: '削峰填谷储能站' },
  { key: 3, name: '南湖充电站', lng: '120.78', lat: '30.74', power: 800, state: 'delay', typeLabel: '光储充一体站' },
  { key: 4, name: '秀洲经开区储能站', lng: '120.71', lat: '30.79', power: 900, state: 'online', typeLabel: '储能电站' },
];

// ---------- 单站系统管理：EMS 时段策略 ----------
export const EMS_PERIODS = [
  { key: 1, name: '谷时充电', start: '00:00', end: '06:00', action: '充电', power: '300 kW', enabled: '启用' },
  { key: 2, name: '峰时放电（上午）', start: '08:00', end: '11:00', action: '放电', power: '250 kW', enabled: '启用' },
  { key: 3, name: '峰时放电（下午）', start: '13:00', end: '19:00', action: '放电', power: '220 kW', enabled: '启用' },
  { key: 4, name: '尖时放电', start: '19:00', end: '22:00', action: '放电', power: '280 kW', enabled: '启用' },
];

// ---------- 单站系统管理：电价 ----------
export const TARIFF_ROWS = [
  { key: 1, kind: '谷', period: '00:00-06:00 / 23:00-24:00', price: 0.32 },
  { key: 2, kind: '平', period: '06:00-08:00 / 11:00-13:00 / 22:00-23:00', price: 0.65 },
  { key: 3, kind: '峰', period: '08:00-11:00 / 13:00-19:00', price: 1.05 },
  { key: 4, kind: '尖', period: '19:00-22:00', price: 1.28 },
];

// ---------- 单站系统管理：电量矫正 ----------
export const CORRECTIONS = [
  { key: 1, time: '2026-10-08 00:00', meter: '储能进线电表 M-02', before: 962.3, after: 961.8, reason: '通信中断补录', operator: '李文杰' },
  { key: 2, time: '2026-09-28 00:00', meter: '并网柜电表 M-01', before: 1284.6, after: 1285.1, reason: 'CT 变比修正', operator: '周翔宇' },
];

// ---------- 单站系统管理：收益重算 ----------
export const RECALC_RECORDS = [
  { key: 1, time: '2026-10-01 02:00', range: '2026-09-01 至 2026-09-30', before: 48620, after: 49230, operator: '系统' },
  { key: 2, time: '2026-09-01 02:00', range: '2026-08-01 至 2026-08-31', before: 48700, after: 48620, operator: '系统' },
];

// ---------- 单站系统管理：告警清理 ----------
export const CLEAR_RECORDS = [
  { key: 1, time: '2026-10-09 18:00', scope: '已恢复告警', count: 36, operator: '周翔宇' },
  { key: 2, time: '2026-09-30 18:00', scope: '90 天前历史告警', count: 128, operator: '系统' },
];

// ---------- 电池分析：落后单体 ----------
export const LAGGING_CELLS = [
  { key: 1, rack: 'RACK-05', cell: 38, voltage: 3.276, diff: -55, temp: 31.8, trend: '持续偏低' },
  { key: 2, rack: 'RACK-02', cell: 61, voltage: 3.301, diff: -30, temp: 32.4, trend: '波动' },
  { key: 3, rack: 'RACK-04', cell: 12, voltage: 3.318, diff: -13, temp: 30.2, trend: '稳定' },
];

// ---------- 健康度 ----------
export const HEALTH_ROWS = [
  { key: 1, device: '电池堆 RACK-01', soh: 98.2, cycles: 412, score: 96, grade: '优' },
  { key: 2, device: '电池堆 RACK-02', soh: 97.4, cycles: 418, score: 93, grade: '优' },
  { key: 3, device: '电池堆 RACK-05', soh: 94.8, cycles: 431, score: 82, grade: '良' },
  { key: 4, device: 'PCS-03', soh: 97.9, cycles: 396, score: 90, grade: '优' },
];

// ---------- 运营分析：月度电量与收益趋势 ----------
export const MONTH_TREND = [
  { month: '04月', charge: 18200, discharge: 16400, income: 42800 },
  { month: '05月', charge: 19600, discharge: 17800, income: 46500 },
  { month: '06月', charge: 21400, discharge: 19200, income: 50100 },
  { month: '07月', charge: 22680, discharge: 20040, income: 55140 },
  { month: '08月', charge: 20460, discharge: 18310, income: 49230 },
  { month: '09月', charge: 21840, discharge: 19620, income: 52960 },
];

// ============================== A 批次：单站系统管理 · 二次口令 / EMS 复归 ==============================
// 依据《云平台（能源管家）需求文档》2.7.7 系统管理：二次口令（可修改二次口令）、EMS 复归（EMS 复归操作）

export const getSecPwdPolicy = () => ({
  rule: '6~12 位，建议字母 + 数字组合',
  validDays: 90,     // 口令有效期（天）
  failLock: 5,       // 连续错误锁定次数
  lockMinutes: 30,   // 锁定时长（分钟）
  scope: ['控制中心指令下发', '设备读写', '故障复归', 'EMS 复归'],
});

export const SEC_PWD_RECORDS = [
  { key: 1, time: '2026-09-12 10:24:08', user: 'admin', action: '修改二次口令', ip: '192.168.0.21', result: '成功' },
  { key: 2, time: '2026-08-03 16:51:33', user: 'ops01', action: '重置二次口令', ip: '192.168.0.35', result: '成功' },
  { key: 3, time: '2026-07-18 09:02:47', user: 'admin', action: '修改二次口令', ip: '192.168.0.21', result: '失败（原口令错误）' },
];

export const EMS_RESET_RECORDS = [
  { key: 1, time: '2026-10-09 14:32:10', scope: '全站 EMS', user: 'admin', before: '策略停止 · 告警 2 条', result: '成功' },
  { key: 2, time: '2026-10-02 08:15:44', scope: 'PCS-03 故障复归', user: 'ops01', before: 'PCS-03 故障闭锁', result: '成功' },
];

// ============================== A 批次：控制中心 · 高级功能 ==============================
// 依据《云平台单站控制（削峰填谷 / 光储充）需求文档》高级功能（页 51-57）
export const getAdvancedFeatures = () => ({
  // 1. 时段需量保护、时段逆功率保护
  periodProtect: {
    enabled: false,
    rows: [
      { key: 1, period: '00:00-06:00', demand: '420 kW', reverse: '20 kW' },
      { key: 2, period: '08:00-11:00', demand: '380 kW', reverse: '30 kW' },
      { key: 3, period: '19:00-22:00', demand: '—（沿用全时段）', reverse: '—（沿用全时段）' },
    ],
  },
  // 2. 动态多级保护
  dynamicMulti: { enabled: false, contract: '最大需量合同', monthlyMaxDemand: 468 },
  // 3. 无功控制
  varControl: {
    enabled: false,
    mode: '固定功率因数',
    fixedVar: 50,        // kVar
    fixedPf: 0.98,
    standard: '380~420', // V
    lowV: 360, lowVar: 80,
    highV: 430, highVar: -80,
  },
  // 4. 时段 SOC 上下限
  periodSoc: {
    enabled: false,
    rows: [
      { key: 1, period: '00:00-06:00', socHigh: 95, socLow: 10 },
      { key: 2, period: '19:00-22:00', socHigh: 90, socLow: 20 },
    ],
  },
  // 5. 储能功率按权重分配
  weightAlloc: {
    enabled: false,
    weights: [
      { key: 'PCS-01', name: 'PCS-01', weight: 1 },
      { key: 'PCS-02', name: 'PCS-02', weight: 1 },
      { key: 'PCS-03', name: 'PCS-03', weight: 4 },
    ],
  },
  // 6. 电池降功率保护
  degradeProtect: {
    enabled: false,
    rules: [
      { key: 1, type: '充电 · 电压', condition: '单体电压 ≥ 3.55 V', power: '100 kW' },
      { key: 2, type: '充电 · 电压', condition: '单体电压 ≥ 3.60 V', power: '50 kW' },
      { key: 3, type: '温度（不分充放电）', condition: '单体温度 ≥ 50 ℃', power: '80 kW' },
    ],
  },
  // 7. 掉电保护（储能电池低电保护）
  lowPower: {
    enabled: false,
    voltage: 3.20,   // V 触发补电的单体电压
    target: 3.30,    // V 补电目标
    power: 20,       // kW 补电功率
  },
});

// ============================== B 批次：分析中心 · 电池分析 ==============================
// 依据《云平台（能源管家）需求文档》2.13 电池分析：电压分析 / 电流分析 / 温度分析 / 落后电池报表

// ---- 2.13.1 电压分析 ----
// 电堆电压预警：极差 = 该电堆下最大电池簇电压 - 最小电池簇电压；标准差 σ
export const getStackVoltageWarn = () => ({
  range: 68,    // mV
  std: 14.2,    // mV
  clusterMax: 717.5, clusterMin: 712.6,   // V 簇端电压极值
  cellMax: 3.362, cellMin: 3.294,         // V 簇内单体电压极值
});

// 电池簇电压预警：簇内单体电压 平均 / 最大 / 最小
export const getClusterVoltageSeries = () => ([
  { clock: '00:00', avg: 3.322, max: 3.346, min: 3.298 },
  { clock: '03:00', avg: 3.318, max: 3.340, min: 3.292 },
  { clock: '06:00', avg: 3.335, max: 3.361, min: 3.304 },
  { clock: '09:00', avg: 3.328, max: 3.352, min: 3.299 },
  { clock: '12:00', avg: 3.330, max: 3.355, min: 3.301 },
  { clock: '15:00', avg: 3.334, max: 3.362, min: 3.306 },
  { clock: '18:00', avg: 3.326, max: 3.349, min: 3.296 },
  { clock: '21:00', avg: 3.318, max: 3.341, min: 3.289 },
]);

// 组端电压分析：同一电堆下各电池簇电压一致性
export const getClusterVoltageConsistency = () => ([
  { name: 'RACK-01', voltage: 716.2 },
  { name: 'RACK-02', voltage: 714.8 },
  { name: 'RACK-03', voltage: 717.5 },
  { name: 'RACK-04', voltage: 713.9 },
  { name: 'RACK-05', voltage: 712.6 },
]);

// 电池簇单体压差趋势：同一单体不同日期的压差分布趋势（单位 V）
export const getCellVoltageDiffTrend = () => ([
  { day: '10-05', c38: 0.086, c61: 0.045, c12: 0.021 },
  { day: '10-06', c38: 0.094, c61: 0.049, c12: 0.024 },
  { day: '10-07', c38: 0.103, c61: 0.054, c12: 0.023 },
  { day: '10-08', c38: 0.112, c61: 0.059, c12: 0.027 },
  { day: '10-09', c38: 0.124, c61: 0.064, c12: 0.028 },
  { day: '10-10', c38: 0.138, c61: 0.071, c12: 0.030 },
  { day: '10-11', c38: 0.152, c61: 0.078, c12: 0.029 },
]);

// ---- 2.13.2 电流分析 ----
// 电堆电流预警：极差 / 标准差（单位 A）
export const getStackCurrentWarn = () => ({
  range: 12.4,
  std: 3.6,
  clusterMax: 121.0, clusterMin: 113.6,
});

// 组端电流分析：同一电堆下不同电池簇的电流一致性
export const getClusterCurrentSeries = () => ([
  { clock: '00:00', r1: 118.2, r2: 116.4, r3: 121.0, r4: 115.2, r5: 113.6 },
  { clock: '03:00', r1: 117.6, r2: 115.9, r3: 120.3, r4: 114.7, r5: 112.8 },
  { clock: '06:00', r1: 120.4, r2: 118.8, r3: 122.6, r4: 117.5, r5: 116.2 },
  { clock: '09:00', r1: 119.1, r2: 117.3, r3: 121.4, r4: 116.2, r5: 114.6 },
  { clock: '12:00', r1: 118.8, r2: 116.9, r3: 121.1, r4: 115.8, r5: 114.1 },
  { clock: '15:00', r1: 119.6, r2: 117.8, r3: 121.8, r4: 116.6, r5: 115.0 },
  { clock: '18:00', r1: 117.9, r2: 116.1, r3: 120.6, r4: 114.9, r5: 113.2 },
  { clock: '21:00', r1: 117.2, r2: 115.4, r3: 119.8, r4: 114.2, r5: 112.4 },
]);

// ---- 2.13.3 温度分析 ----
// 电池簇温度预警：温差（单位 ℃）
export const getStackTempWarn = () => ({
  range: 6.8,
  std: 1.9,
  clusterMax: 30.2, clusterMin: 23.4,
});

// 温差预警：堆内多簇一段时间范围内的温差趋势
export const getClusterTempSeries = () => ([
  { clock: '00:00', r1: 27.2, r2: 28.1, r3: 29.4, r4: 28.8, r5: 30.2 },
  { clock: '03:00', r1: 26.8, r2: 27.6, r3: 29.0, r4: 28.3, r5: 29.6 },
  { clock: '06:00', r1: 28.4, r2: 29.3, r3: 30.6, r4: 29.9, r5: 31.4 },
  { clock: '09:00', r1: 29.6, r2: 30.5, r3: 31.8, r4: 31.1, r5: 32.6 },
  { clock: '12:00', r1: 30.8, r2: 31.7, r3: 33.0, r4: 32.3, r5: 33.8 },
  { clock: '15:00', r1: 31.4, r2: 32.2, r3: 33.6, r4: 32.9, r5: 34.4 },
  { clock: '18:00', r1: 30.2, r2: 31.0, r3: 32.4, r4: 31.7, r5: 33.2 },
  { clock: '21:00', r1: 28.6, r2: 29.4, r3: 30.8, r4: 30.1, r5: 31.6 },
]);

// 单体温升预警：簇内多单体一段时间范围内的温升趋势（相对静置基准，单位 ℃）
export const getCellTempRise = () => ([
  { clock: '00:00', c1: 0.0, c38: 0.2, c61: 0.1, c104: 0.3 },
  { clock: '03:00', c1: 0.1, c38: 0.4, c61: 0.2, c104: 0.5 },
  { clock: '06:00', c1: 0.3, c38: 0.8, c61: 0.4, c104: 0.9 },
  { clock: '09:00', c1: 0.6, c38: 1.4, c61: 0.8, c104: 1.6 },
  { clock: '12:00', c1: 0.9, c38: 2.1, c61: 1.2, c104: 2.4 },
  { clock: '15:00', c1: 1.1, c38: 2.6, c61: 1.5, c104: 2.9 },
  { clock: '18:00', c1: 0.7, c38: 1.7, c61: 1.0, c104: 1.9 },
  { clock: '21:00', c1: 0.3, c38: 0.9, c61: 0.5, c104: 1.0 },
]);

// ---- 2.13.4 落后电池报表 ----
// 遍历所有电池簇，找该簇中当天电压最低的 5 个电芯，按电压从低到高逐行排列
export const LAGGING_REPORT = [
  { key: 1, rack: 'RACK-01', cell: 87, voltage: 3.271, clusterAvg: 3.326, diff: 55, time: '2026-10-11 23:59:30' },
  { key: 2, rack: 'RACK-01', cell: 12, voltage: 3.288, clusterAvg: 3.326, diff: 38, time: '2026-10-11 23:59:30' },
  { key: 3, rack: 'RACK-01', cell: 63, voltage: 3.301, clusterAvg: 3.326, diff: 25, time: '2026-10-11 23:59:30' },
  { key: 4, rack: 'RACK-01', cell: 44, voltage: 3.312, clusterAvg: 3.326, diff: 14, time: '2026-10-11 23:59:30' },
  { key: 5, rack: 'RACK-01', cell: 99, voltage: 3.318, clusterAvg: 3.326, diff: 8, time: '2026-10-11 23:59:30' },
  { key: 6, rack: 'RACK-02', cell: 61, voltage: 3.291, clusterAvg: 3.331, diff: 40, time: '2026-10-11 23:59:30' },
  { key: 7, rack: 'RACK-02', cell: 8, voltage: 3.304, clusterAvg: 3.331, diff: 27, time: '2026-10-11 23:59:30' },
  { key: 8, rack: 'RACK-02', cell: 75, voltage: 3.315, clusterAvg: 3.331, diff: 16, time: '2026-10-11 23:59:30' },
  { key: 9, rack: 'RACK-02', cell: 30, voltage: 3.322, clusterAvg: 3.331, diff: 9, time: '2026-10-11 23:59:30' },
  { key: 10, rack: 'RACK-02', cell: 52, voltage: 3.327, clusterAvg: 3.331, diff: 4, time: '2026-10-11 23:59:30' },
];

// ---- 电池分析：秒级趋势 / 集群分析 / 分布 / 曲线 / 明细表（各子页 demo 数据） ----
// 稳定伪随机（同 seed 恒等输出），保证演示数据形状自然且可复现
const rnd01 = (seed) => { const x = Math.sin(seed * 12.9898) * 43758.5453; return x - Math.floor(x); };
const idx = (n) => [...Array(n).keys()];
const secClock = (base, step, i) => {
  const s = base + i * step;
  const p = (n) => String(n).padStart(2, '0');
  return `${p(Math.floor(s / 3600))}:${p(Math.floor((s % 3600) / 60))}:${p(s % 60)}`;
};

// 电堆电压预警·秒级趋势：y=电压/V，图例 极差 / 标准差
export const getStackVoltageWarnSeries = () => idx(32).map((i) => ({
  time: secClock(8 * 3600, 45, i),
  range: +(0.055 + rnd01(i + 1) * 0.04).toFixed(3),
  std: +(0.011 + rnd01(i + 7) * 0.008).toFixed(3),
}));

// 电堆电流预警·秒级趋势：y=电流/A，图例 极差 / 标准差（后段出现放电尖峰）
export const getStackCurrentWarnSeries = () => idx(32).map((i) => {
  const spike = i > 20 && i < 28 ? 6.4 : 0;
  return {
    time: secClock(8 * 3600, 45, i),
    range: +(0.4 + rnd01(i + 3) * 1.2 + spike).toFixed(2),
    std: +(0.2 + rnd01(i + 11) * 0.9 + spike * 0.4).toFixed(2),
  };
});

// 电池簇单体压差分布：y=压差/V，x=单体编号（簇内 240 个单体）
export const getCellVoltageDiffDistribution = () => idx(240).map((i) => ({
  cell: i + 1,
  diff: +(0.02 + rnd01((i + 1) * 1.3) * 0.26).toFixed(3),
}));

// 单体电压分布：y=电压/mV，x=单体编号
export const getCellVoltageDistribution = () => idx(240).map((i) => ({
  cell: i + 1,
  voltage: +(3300 + rnd01((i + 1) * 2.1) * 90).toFixed(1),
}));

// 单体电压一致性明细表：单体编号 / 单体电压(mV) / 平均电池电压(mV) / 电池组 / 一致性
export const getCellVoltageTable = () => idx(240).map((i) => {
  const v = +(3300 + rnd01((i + 1) * 2.1) * 90).toFixed(1);
  const dev = Math.abs(v - 3348);
  return {
    key: i + 1, cell: i + 1, voltage: v, avg: 3348,
    group: (i % 12) + 1,
    consistency: dev > 60 ? '差' : dev > 40 ? '中' : dev > 18 ? '良' : '优',
  };
});

// 单体电压曲线：y=电压/V，x=日期（同一单体的日期间趋势）
export const getCellVoltageCurve = () => {
  const days = ['11-09', '11-10', '11-11', '11-12', '11-13', '11-14', '11-15', '11-16', '11-17', '11-18', '11-19', '11-20', '11-21', '11-22'];
  return days.map((d, i) => ({ time: d, v: +(3.33 + Math.sin(i / 2) * 0.012 + rnd01(i + 5) * 0.006).toFixed(3) }));
};

// 电池簇单体电流集群分析：簇内单体电流 平均 / 最大 / 最小（A）
export const getCellCurrentClusterSeries = () => ([
  { clock: '00:00', avg: 118.2, max: 119.4, min: 117.0 },
  { clock: '03:00', avg: 117.4, max: 118.6, min: 116.2 },
  { clock: '06:00', avg: 120.1, max: 121.5, min: 118.8 },
  { clock: '09:00', avg: 119.0, max: 120.2, min: 117.8 },
  { clock: '12:00', avg: 118.6, max: 119.9, min: 117.4 },
  { clock: '15:00', avg: 119.4, max: 120.7, min: 118.1 },
  { clock: '18:00', avg: 117.9, max: 119.1, min: 116.7 },
  { clock: '21:00', avg: 117.0, max: 118.2, min: 115.8 },
]);

// 电池簇单体电流压差分布：y=压差/A，x=单体编号
export const getCellCurrentDiffDistribution = () => idx(240).map((i) => ({
  cell: i + 1,
  diff: +(0.2 + rnd01((i + 1) * 1.7) * 1.4).toFixed(2),
}));

// 电池簇单体电流压差趋势：同一单体不同日期的压差趋势（A）
export const getCellCurrentDiffTrend = () => ([
  { day: '10-05', c38: 0.62, c61: 0.34, c12: 0.18 },
  { day: '10-06', c38: 0.68, c61: 0.38, c12: 0.20 },
  { day: '10-07', c38: 0.74, c61: 0.41, c12: 0.22 },
  { day: '10-08', c38: 0.81, c61: 0.45, c12: 0.21 },
  { day: '10-09', c38: 0.88, c61: 0.49, c12: 0.24 },
  { day: '10-10', c38: 0.95, c61: 0.53, c12: 0.26 },
  { day: '10-11', c38: 1.04, c61: 0.57, c12: 0.25 },
]);

// 单体电流分布：y=电流/A，x=单体编号
export const getCellCurrentDistribution = () => idx(240).map((i) => ({
  cell: i + 1,
  current: +(114 + rnd01((i + 1) * 2.7) * 10).toFixed(2),
}));

// 单体电流一致性明细表：单体编号 / 单体电流(A) / 平均电池电流(A) / 电池组 / 一致性
export const getCellCurrentTable = () => idx(240).map((i) => {
  const c = +(114 + rnd01((i + 1) * 2.7) * 10).toFixed(2);
  const dev = Math.abs(c - 119);
  return {
    key: i + 1, cell: i + 1, current: c, avg: 119,
    group: (i % 12) + 1,
    consistency: dev > 6 ? '差' : dev > 4 ? '中' : dev > 2 ? '良' : '优',
  };
});

// 单体电流曲线：y=电流/A，x=日期
export const getCellCurrentCurve = () => {
  const days = ['11-09', '11-10', '11-11', '11-12', '11-13', '11-14', '11-15', '11-16', '11-17', '11-18', '11-19', '11-20', '11-21', '11-22'];
  return days.map((d, i) => ({ time: d, v: +(118 + Math.sin(i / 2) * 3 + rnd01(i + 3) * 1.5).toFixed(2) }));
};

// 电池簇温度分析：簇内单体温度 平均 / 最大 / 最小（℃）
export const getClusterTempAvgMaxMin = () => ([
  { clock: '00:00', avg: 27.5, max: 28.4, min: 26.6 },
  { clock: '03:00', avg: 26.9, max: 27.8, min: 26.0 },
  { clock: '06:00', avg: 28.6, max: 29.6, min: 27.7 },
  { clock: '09:00', avg: 30.1, max: 31.2, min: 29.1 },
  { clock: '12:00', avg: 31.8, max: 32.9, min: 30.7 },
  { clock: '15:00', avg: 32.6, max: 33.7, min: 31.5 },
  { clock: '18:00', avg: 30.4, max: 31.4, min: 29.4 },
  { clock: '21:00', avg: 28.3, max: 29.2, min: 27.4 },
]);

// 电池簇温差预警：一段时间范围内的温差趋势（℃）
export const getClusterTempDiffSeries = () => idx(24).map((i) => ({
  clock: secClock(11 * 3600, 600, i),
  diff: +(0.6 + Math.abs(Math.sin(i * 0.9)) * 5.2 + rnd01(i + 2) * 0.8).toFixed(1),
}));

// 单体温度分布：y=温度/℃，x=单体编号
export const getCellTempDistribution = () => idx(240).map((i) => ({
  cell: i + 1,
  temp: +(20 + rnd01((i + 1) * 1.9) * 12).toFixed(1),
}));

// 单体温度一致性明细表：单体编号 / 单体温度(℃) / 平均电池温度(℃) / 电池组 / 一致性
export const getCellTempTable = () => idx(240).map((i) => {
  const t = +(20 + rnd01((i + 1) * 1.9) * 12).toFixed(1);
  const dev = Math.abs(t - 26);
  return {
    key: i + 1, cell: i + 1, temp: t, avg: 26,
    group: (i % 12) + 1,
    consistency: dev > 5 ? '差' : dev > 3.5 ? '中' : dev > 1.8 ? '良' : '优',
  };
});

// 单体温度曲线：y=温度/℃，x=日期
export const getCellTempCurve = () => {
  const days = ['11-09', '11-10', '11-11', '11-12', '11-13', '11-14', '11-15', '11-16', '11-17', '11-18', '11-19', '11-20', '11-21', '11-22'];
  return days.map((d, i) => ({ time: d, v: +(22.5 + Math.sin(i / 2) * 1.2 + rnd01(i + 9) * 0.5).toFixed(1) }));
};

// 落后电池报表：首列日期，其后按电池簇分组多列（每簇 单体编号 / 最低电压 / 首次发生时间，5 行，电压由低到高）
export const getLaggingReport = () => {
  const clusters = ['1#电池簇', '2#电池簇', '3#电池簇', '4#电池簇', '5#电池簇'];
  const date = '2026-10-19';
  const p = (n) => String(n).padStart(2, '0');
  const rows = idx(5).map((r) => {
    const row = { key: r, date, dateSpan: r === 0 ? 5 : 0 };
    for (let ci = 0; ci < clusters.length; ci += 1) {
      row[`c${ci + 1}Cell`] = ((ci * 47 + r * 13 + 8) % 240) + 1;
      row[`c${ci + 1}Voltage`] = +(3.19 + ci * 0.003 + r * 0.007).toFixed(3);
      row[`c${ci + 1}Time`] = `${date} ${p(7 + ci)}:${p((ci * 7 + r * 9) % 60)}:${p((ci * 13 + r * 17) % 60)}`;
    }
    return row;
  });
  const columns = [
    { title: '日期', dataIndex: 'date', width: 130, rowSpanKey: 'dateSpan' },
    ...clusters.map((name, ci) => ({
      title: name,
      children: [
        { title: '单体编号', dataIndex: `c${ci + 1}Cell`, num: true },
        { title: '最低电压 (V)', dataIndex: `c${ci + 1}Voltage` },
        { title: '首次发生时间', dataIndex: `c${ci + 1}Time` },
      ],
    })),
  ];
  return { columns, rows };
};

// ============================== B 批次：分析中心 · 健康度分析 ==============================
// 依据《云平台（能源管家）需求文档》2.14 健康度分析：
// 从寿命、效率、安全性、可靠性四个指标对比实际值与预测值，不符则预警

// 四指标预警规则（doc01 2.14）：实际寿命 / 实际运行效率低于预测值 → 预警；
// 实际安全性 / 可靠性（当月告警次数类指标）高于预测值 → 预警。
// 未配置预测值（或未关联站点）的资产无健康度分析结果，四指标以「—」占位。
export const HEALTH_ASSETS = [
  {
    key: 1, site: '余新镇储能站', code: 'AS-2023-001', name: '1# 储能柜', factory: '2023-05-12',
    predictCfg: '已配置', life: 92.4, eff: 88.6, safety: 96.2, reliability: 94.1,
    lifePred: 95.0, effPred: 90.5, safetyPred: 97.0, reliabilityPred: 95.0,
  },
  {
    key: 2, site: '王江泾光储站', code: 'AS-2023-002', name: '2# 储能柜', factory: '2023-06-20',
    predictCfg: '已配置', life: 96.1, eff: 93.2, safety: 94.6, reliability: 92.8,
    lifePred: 95.2, effPred: 91.0, safetyPred: 95.6, reliabilityPred: 95.6,
  },
  {
    key: 3, site: '桐乡光伏站', code: 'AS-2024-011', name: '3# 储能柜', factory: '2024-03-08',
    predictCfg: '已配置', life: 96.5, eff: 94.2, safety: 97.4, reliability: 96.8,
    lifePred: 95.0, effPred: 93.0, safetyPred: 96.0, reliabilityPred: 96.0,
  },
  {
    key: 4, site: '钱塘新区储能站', code: 'AS-2024-027', name: '4# 储能柜', factory: '2024-09-15',
    predictCfg: '未配置', life: '—', eff: '—', safety: '—', reliability: '—',
    lifePred: '—', effPred: '—', safetyPred: '—', reliabilityPred: '—',
  },
];

// 单资产当年 4 个指标 实际值 vs 预测值 对比曲线（详情二级页）
export const getHealthTrend = () => ([
  { month: '04月', lifeA: 97.6, lifeP: 97.2, effA: 94.1, effP: 93.0, safetyA: 100, safetyP: 100, reliA: 99.2, reliP: 98.0 },
  { month: '05月', lifeA: 96.9, lifeP: 96.6, effA: 93.2, effP: 92.4, safetyA: 99.4, safetyP: 100, reliA: 98.6, reliP: 97.8 },
  { month: '06月', lifeA: 95.8, lifeP: 96.0, effA: 92.1, effP: 91.8, safetyA: 98.8, safetyP: 99.6, reliA: 97.9, reliP: 97.4 },
  { month: '07月', lifeA: 95.2, lifeP: 95.4, effA: 91.4, effP: 91.2, safetyA: 98.2, safetyP: 99.2, reliA: 97.1, reliP: 97.0 },
  { month: '08月', lifeA: 94.4, lifeP: 94.8, effA: 90.6, effP: 90.6, safetyA: 97.5, safetyP: 98.8, reliA: 96.4, reliP: 96.6 },
  { month: '09月', lifeA: 93.6, lifeP: 94.2, effA: 89.8, effP: 90.0, safetyA: 96.9, safetyP: 98.4, reliA: 95.6, reliP: 96.2 },
  { month: '10月', lifeA: 92.4, lifeP: 95.0, effA: 88.6, effP: 90.5, safetyA: 96.2, safetyP: 95.0, reliA: 94.1, reliP: 95.0 },
]);

// ============================== B 批次：单站系统管理 · 拓扑图配置 ==============================
// 依据《云平台单站控制（削峰填谷）需求文档》拓扑图配置（页 47-48）：
// 「拓扑图」依据用户手动配置展示现场接线情况与各设备实时数据，展示内容在【系统管理-拓扑图配置】中配置。

// 左侧可拖入画布的图元
export const TOPO_PALETTE = [
  { key: 'grid', name: '电网' },
  { key: 'transformer', name: '变压器' },
  { key: 'storage', name: '储能节点' },
  { key: 'pcs', name: 'PCS' },
  { key: 'battery', name: '电池簇' },
  { key: 'pv', name: '光伏节点' },
  { key: 'load', name: '负载节点' },
  { key: 'meter', name: '电表' },
];

// 画布上已放置的图元（演示：与强电结构一致的初始布局）
export const TOPO_NODES = [
  { key: 'n-grid', type: 'grid', name: '电网 10kV', x: 40, y: 150 },
  { key: 'n-tr', type: 'transformer', name: '变压器 1250kVA', x: 200, y: 150 },
  { key: 'n-bus', type: 'bus', name: '并网母线', x: 360, y: 150 },
  { key: 'n-pcs', type: 'pcs', name: 'PCS-01', x: 520, y: 70 },
  { key: 'n-bat', type: 'battery', name: '电池堆 RACK-01', x: 520, y: 230 },
  { key: 'n-load', type: 'load', name: '园区负载', x: 700, y: 150 },
];

// 图元之间已绘制的连接线
export const TOPO_LINKS = [
  { from: 'n-grid', to: 'n-tr' },
  { from: 'n-tr', to: 'n-bus' },
  { from: 'n-bus', to: 'n-pcs' },
  { from: 'n-pcs', to: 'n-bat' },
  { from: 'n-bus', to: 'n-load' },
];

// 可在「拓扑图」上展示的设备实时数据项（配置勾选项）
export const TOPO_SHOW_OPTIONS = [
  { key: 'power', label: '有功功率', checked: true },
  { key: 'soc', label: 'SOC', checked: true },
  { key: 'voltage', label: '电压', checked: true },
  { key: 'current', label: '电流', checked: false },
  { key: 'temp', label: '温度', checked: false },
  { key: 'state', label: '运行状态', checked: true },
  { key: 'alarm', label: '告警标识', checked: true },
];

// ============================== D 批次：数据大屏 / 站点地图 / 三维（doc01 2.1 / 2.4 / 2.5）==============================
// 数据大屏 · 近 7 日（不含今天）收益与充放电量
export const SCREEN_DAILY = [
  { day: '10-05', income: 24800, charge: 4260, discharge: 3880 },
  { day: '10-06', income: 26120, charge: 4410, discharge: 4020 },
  { day: '10-07', income: 22940, charge: 3980, discharge: 3620 },
  { day: '10-08', income: 27360, charge: 4620, discharge: 4180 },
  { day: '10-09', income: 25680, charge: 4340, discharge: 3960 },
  { day: '10-10', income: 23920, charge: 4120, discharge: 3760 },
  { day: '10-11', income: 26840, charge: 4480, discharge: 4080 },
];

// 社会效益（doc01 2.1 口径）：由多站累计总放电量折算
export const getSocialBenefit = (totalDischargeKwh = 1864200) => {
  const co2 = Math.round(totalDischargeKwh * 0.581); // kg
  return {
    totalDischarge: totalDischargeKwh,
    coalSaving: Math.round(totalDischargeKwh * 0.284), // 节约标准煤 kg
    co2Reduction: co2,                                  // 二氧化碳减排 kg
    trees: Math.round(co2 / 18.3),                      // 种树量 棵
  };
};

// 多站累计收益排名（top5，doc01 2.1 收益排名）
export const getIncomeRanking = () => [
  { name: '海宁工业园站', value: 186400 },
  { name: '余新镇储能站', value: 96200 },
  { name: '南湖充电站', value: 74800 },
  { name: '杭州临平储能站', value: 41200 },
  { name: '绍兴柯桥微网站', value: 28600 },
];

// 站点地图指标（doc01 2.4，隐藏站点不计入）
export const getMapMetrics = () => ({
  count: STATIONS.length,
  power: '3.45 MW',
  capacity: '5.9 MWh',
  totalEnergy: '1,365 MWh',
  chargeEnergy: '704 MWh',
  dischargeEnergy: '661 MWh',
  totalIncome: '47.6 万元',
  incomeYesterday: '35,790 元',
});

// 站点地图点位（补充告警标识：告警中=红、正常=绿）
export const MAP_POINTS_ALARM = MAP_POINTS.map((p) => ({ ...p, alarmText: p.state === 'delay' ? '告警中' : '正常' }));

// 三维可视化 · 三层层级（站点 → 箱体 → 设备，doc01 2.5）
export const SCENE_TREE = [
  {
    key: 'st-hn', title: '海宁工业园站（站点层）',
    children: [
      {
        key: 'ct-1', title: '储能集装箱 #1（箱体层）',
        children: [
          { key: 'd-r1', title: '电池堆 #1 · 单体 104 串（设备层）' },
          { key: 'd-r2', title: '电池堆 #2 · 单体 104 串' },
          { key: 'd-p1', title: 'PCS #1 · 250 kW' },
        ],
      },
      {
        key: 'ct-2', title: '储能集装箱 #2（箱体层）',
        children: [
          { key: 'd-r3', title: '电池堆 #3 · 单体 104 串' },
          { key: 'd-p2', title: 'PCS #2 · 250 kW' },
        ],
      },
    ],
  },
];

// 三维可视化 · 站点层级指标（doc01 2.5）
export const get3dMetrics = () => ({
  location: '浙江省嘉兴市海宁市经济开发区',
  power: '1250 kW',
  capacity: '2500 kWh',
  onlineAt: '2024-06-18',
  incomeTotal: '18.64 万元',
  incomeYesterday: '26,840 元',
  energyTotal: '1,024 MWh',
  alarmToday: 2,
});

// ============================== 多站概览（doc01 2.2）==============================
export const getMultiMetrics = () => ({
  count: 3,
  power: '2.55 MW',
  capacity: '4.1 MWh',
  energy: '1,024 MWh',
  incomeTotal: '35.7 万元',
  incomeYesterday: '26,840 元',
});

// 各站点收益（按分时电量 × 分时电价）与每 kWh 收益（收益排名）
export const getStationIncome = () => [
  { name: '海宁工业园站', income: 18640, perKwh: 0.62 },
  { name: '余新镇储能站', income: 9620, perKwh: 0.58 },
  { name: '南湖充电站', income: 7480, perKwh: 0.55 },
];

// 多站充放电量（按时间维度切换）
export const MULTI_ENERGY = {
  近7日: [
    { label: '10-05', charge: 4260, discharge: 3880 },
    { label: '10-06', charge: 4410, discharge: 4020 },
    { label: '10-07', charge: 3980, discharge: 3620 },
    { label: '10-08', charge: 4620, discharge: 4180 },
    { label: '10-09', charge: 4340, discharge: 3960 },
    { label: '10-10', charge: 4120, discharge: 3760 },
    { label: '10-11', charge: 4480, discharge: 4080 },
  ],
  当月: [
    { label: '第1周', charge: 28640, discharge: 26120 },
    { label: '第2周', charge: 30180, discharge: 27460 },
    { label: '第3周', charge: 29760, discharge: 27040 },
    { label: '第4周', charge: 30210, discharge: 27580 },
  ],
  当年: [
    { label: '04月', charge: 18200, discharge: 16400 },
    { label: '05月', charge: 19600, discharge: 17800 },
    { label: '06月', charge: 21400, discharge: 19200 },
    { label: '07月', charge: 22680, discharge: 20040 },
    { label: '08月', charge: 20460, discharge: 18310 },
    { label: '09月', charge: 21840, discharge: 19620 },
    { label: '10月', charge: 118790, discharge: 107660 },
  ],
  全部: [
    { label: '2023', charge: 186400, discharge: 168200 },
    { label: '2024', charge: 268600, discharge: 242800 },
    { label: '2025', charge: 342100, discharge: 309400 },
    { label: '2026', charge: 291300, discharge: 263150 },
  ],
};

// 系统效率趋势：由同口径充放电量按公式派生（勿再硬编码，避免与电量柱对不上）
export const getMultiEfficiencyTrend = () => withSystemEfficiency(MULTI_ENERGY['近7日']).map((d) => ({
  label: d.label,
  efficiency: d.efficiency,
}));

export const getEfficiencyRanking = () => [
  { name: '海宁工业园站', efficiency: 91.6 },
  { name: '余新镇储能站', efficiency: 90.4 },
  { name: '南湖充电站', efficiency: 89.8 },
];

// 告警：多站次数曲线 / 设备告警分布 / 告警等级分布 / 站点排名
export const getAlarmTrend = () => [
  { label: '10-05', count: 2 },
  { label: '10-06', count: 1 },
  { label: '10-07', count: 3 },
  { label: '10-08', count: 2 },
  { label: '10-09', count: 1 },
  { label: '10-10', count: 4 },
  { label: '10-11', count: 2 },
];

export const getAlarmByType = () => [
  { name: '电池系统', value: 6 },
  { name: 'PCS 变流器', value: 3 },
  { name: '空调', value: 2 },
  { name: '消防', value: 1 },
];

export const getAlarmByLevel = () => [
  { name: '紧急', value: 2 },
  { name: '重要', value: 4 },
  { name: '次要', value: 6 },
  { name: '提示', value: 3 },
];

export const getAlarmRanking = () => [
  { name: '海宁工业园站', count: 8 },
  { name: '南湖充电站', count: 5 },
  { name: '余新镇储能站', count: 2 },
];

// ============================== 单站概览（doc01 2.3）==============================
// 卡片栏：装机功率 / 装机容量 / 总充放电量 / 累计收益 / 昨日收益 / 今日充放电量 / 投运时间 / 并网点数量
const SINGLE_BASE = { hn: 1, yx: 0.42, nx: 0.66, xz: 0.72 };
const sScale = (n, f) => Math.round(n * f);

export const getSingleCard = (id) => {
  const st = getStation(id);
  const ov = getOverview(id);
  const f = SINGLE_BASE[id] || 1;
  return {
    power: st.rated.power,                                              // 装机功率
    capacity: st.rated.capacity,                                        // 装机容量
    energyTotal: `${(642.1 * f).toFixed(1)} MWh`,                       // 总充放电量
    energyToday: `${(ov.chargeToday + ov.dischargeToday).toLocaleString()} kWh`, // 今日充放电量
    chargeTotal: `${(Number(ov.chargeTotal) * f).toFixed(1)} MWh`,      // 累计充电量
    dischargeTotal: `${(Number(ov.dischargeTotal) * f).toFixed(1)} MWh`, // 累计放电量
    chargeToday: `${ov.chargeToday.toLocaleString()} kWh`,              // 今日充电量
    dischargeToday: `${ov.dischargeToday.toLocaleString()} kWh`,        // 今日放电量
    incomeTotal: `${(ov.incomeTotal / 10000).toFixed(2)} 万元`,          // 累计收益
    incomeYesterday: `${ov.incomeYesterday.toLocaleString()} 元`,        // 昨日收益
    onlineAt: st.onlineAt,                                             // 投运时间
    gridPoints: st.type === 'pvc' ? 2 : 1,                             // 并网点数量
  };
};

// 收益柱状图（按分时电量 × 分时电价，支持时间维度切换）
export const getSingleIncome = (id) => {
  const f = SINGLE_BASE[id] || 1;
  const out = {};
  Object.keys(MULTI_ENERGY).forEach((k) => {
    out[k] = MULTI_ENERGY[k].map((d) => ({ label: d.label, income: sScale(d.discharge * 0.62, f) }));
  });
  return out;
};

// 电量柱状图（多时间维度充放电量）
export const getSingleEnergy = (id) => {
  const f = SINGLE_BASE[id] || 1;
  const out = {};
  Object.keys(MULTI_ENERGY).forEach((k) => {
    out[k] = withSystemEfficiency(
      MULTI_ENERGY[k].map((d) => ({ label: d.label, charge: sScale(d.charge, f), discharge: sScale(d.discharge, f) })),
    );
  });
  return out;
};

// 系统效率：指标 + 折线图 = 同站「近7日」电量按公式派生
export const getSingleEfficiency = (id) => {
  const energy7 = getSingleEnergy(id)['近7日'] || [];
  const trend = energy7.map((d) => ({ label: d.label, efficiency: d.efficiency }));
  const value = trend.length
    ? +(trend.reduce((s, d) => s + d.efficiency, 0) / trend.length).toFixed(1)
    : 0;
  return { value, trend };
};

// 告警指标：曲线图（告警次数）+ 饼图（设备告警分布 / 告警等级分布）
export const getSingleAlarm = (id) => {
  const f = SINGLE_BASE[id] || 1;
  return {
    trend: getAlarmTrend().map((d) => ({ label: d.label, count: Math.max(0, sScale(d.count, f)) })),
    byType: getAlarmByType().map((d) => ({ name: d.name, value: Math.max(0, sScale(d.value, f)) })),
    byLevel: getAlarmByLevel().map((d) => ({ name: d.name, value: Math.max(0, sScale(d.value, f)) })),
  };
};

// ============================== E 批次：监控中心（doc01 2.6 / 2.8 / 2.9）==============================

// 站点行政区域（省 / 市 / 区），供监控中心查询区三级筛选
export const STATION_REGIONS = {
  hn: { province: '浙江省', city: '嘉兴市', district: '海宁市' },
  yx: { province: '浙江省', city: '嘉兴市', district: '南湖区' },
  nx: { province: '浙江省', city: '嘉兴市', district: '南湖区' },
  xz: { province: '浙江省', city: '嘉兴市', district: '秀洲区' },
};

// 站点装机容量（kWh），用于「今日循环次数 = 今日放电量 / 装机容量」
const RATED_KWH = { hn: 2500, yx: 1000, nx: 1600, xz: 1800 };
// 今日告警次数（演示）
const ALARM_TODAY = { hn: 4, yx: 2, nx: 1, xz: 2 };

// 2.6 实时运行：电站列表（字段口径严格按文档：
// 站点名称 / 云站通信状态 / PCS 工作状态 / 总有功功率 / SOC 最高·最低 /
// 今日充放电量 / 空调温度最高·最低 / 今日告警次数 / 今日循环次数）
export const getRealtimeList = () => STATIONS.map((s) => {
  const ov = getOverview(s.id);
  const dev = getDevices(s.id);
  const run = dev.pcs.filter((p) => p.running).length;
  const socs = dev.racks.map((r) => r.soc);
  const temps = dev.hvac.map((a) => a.returnTemp);
  const rated = RATED_KWH[s.id] || 1000;
  const outage = s.comm === 'offline';
  const dash = '-';
  return {
    key: s.id,
    name: s.name,
    typeLabel: s.typeLabel,
    comm: outage ? '中断' : s.comm === 'delay' ? '延迟' : '正常',
    pcsState: outage ? dash : `运行 ${run} / 停机 ${dev.pcs.length - run}`,
    // 点击加号展开的 PCS 运行明细（哪些运行、哪些停机）
    pcsDetail: outage ? [] : dev.pcs.map((p) => ({ name: p.name, running: !!p.running })),
    power: outage ? dash : `${ov.gridPower + ov.storagePower} kW`,
    soc: outage ? dash : `${Math.max(...socs)} / ${Math.min(...socs)}`,
    energy: outage ? dash : `充 ${ov.chargeToday} / 放 ${ov.dischargeToday} kWh`,
    hvac: outage ? dash : `${Math.max(...temps)} / ${Math.min(...temps)} ℃`,
    alarmCount: outage ? dash : ALARM_TODAY[s.id] || 0,
    cycle: outage ? dash : (ov.dischargeToday / rated).toFixed(2),
  };
});

// 2.6 实时运行：点击加号展开的详情（基本信息 + 3 个图表）
export const getRealtimeDetail = (id) => {
  const st = getStation(id);
  const region = STATION_REGIONS[st.id] || {};
  const f = SINGLE_BASE[st.id] || 1;
  // 图一：充放电电量及系统效率（近 7 日）
  // 与多站/单站概览同一套 MULTI_ENERGY，效率严格 = 当日放电/充电，避免「柱起伏、效率几乎不动」的假对应
  const energy = withSystemEfficiency(
    MULTI_ENERGY['近7日'].map((d) => ({
      label: d.label,
      charge: sScale(d.charge, f),
      discharge: sScale(d.discharge, f),
    })),
  );
  // 图二：总有功功率（今日与昨日对比）
  const power = getPcsCurve().map((d) => ({
    clock: d.clock,
    today: d.dcPower,
    yesterday: d.dcPowerYesterday,
  }));
  // 图三：设备故障告警（当日，按发生时间由晚到早，最多 20 条）
  const alarms = getAlarms().today.slice(0, 20);
  return {
    info: [
      ['电站位置', `${region.province || ''}${region.city || ''}${region.district || ''} · ${st.location}`],
      ['投运时间', st.onlineAt],
      ['装机功率', st.rated.power],
      ['装机容量', st.rated.capacity],
    ],
    energy,
    power,
    alarms,
  };
};

// 2.8 视频监控：按站点分组的视频宫格（仅接入摄像头的电站展示）
export const getVideoGrid = () => {
  const map = new Map();
  VIDEO_CHANNELS.forEach((c) => {
    if (!map.has(c.station)) map.set(c.station, []);
    map.get(c.station).push(c);
  });
  return Array.from(map.entries()).map(([station, channels]) => ({ station, channels }));
};

// 2.9 集控管理：站点批量配置电价（谷 / 平 / 峰 / 尖）
export const CENTRAL_TARIFF = {
  valley: { label: '谷', price: 0.32 },
  flat: { label: '平', price: 0.65 },
  peak: { label: '峰', price: 1.05 },
  sharp: { label: '尖', price: 1.28 },
};

// ============================== 报警中心（doc01 2.10 / 2.11）==============================

// 2.10.1 报警列表：今日指标（doc01 文档图为 6 张卡片）
export const getFaultTodayMetrics = () => ({
  alarmToday: 30,     // 今日告警（条）
  pending: 2,         // 发生中（条）
  closed: 28,         // 已结束（条）
  stations: 6,        // 电站总数（座）
  warnStations: 2,    // 当前告警（座）
  todayStations: 4,   // 今日告警（座）
});

// 2.10.1 今日报警统计：0-24 时多站报警次数
export const getFaultHourly = () => [
  { clock: '00:00', count: 0 }, { clock: '01:00', count: 0 }, { clock: '02:00', count: 0 },
  { clock: '03:00', count: 1 }, { clock: '04:00', count: 0 }, { clock: '05:00', count: 1 },
  { clock: '06:00', count: 2 }, { clock: '07:00', count: 1 }, { clock: '08:00', count: 3 },
  { clock: '09:00', count: 4 }, { clock: '10:00', count: 2 }, { clock: '11:00', count: 3 },
  { clock: '12:00', count: 1 }, { clock: '13:00', count: 2 }, { clock: '14:00', count: 3 },
  { clock: '15:00', count: 2 }, { clock: '16:00', count: 1 }, { clock: '17:00', count: 2 },
  { clock: '18:00', count: 3 }, { clock: '19:00', count: 2 }, { clock: '20:00', count: 1 },
  { clock: '21:00', count: 1 }, { clock: '22:00', count: 0 }, { clock: '23:00', count: 0 },
];

// 2.10.1 今日设备报警统计（0 时至当前发生告警的设备占比，饼图；设备类型对齐文档）
export const getFaultTodayByDevice = () => [
  { name: '站控设备', value: 0 },
  { name: 'PCS', value: 0 },
  { name: 'BMS', value: 14 },
  { name: '双向电表', value: 0 },
  { name: '空调', value: 16 },
  { name: '摄像头', value: 0 },
];

// 2.10.1 今日报警等级统计（A / B / C / 未分级 占比，环形图）
export const getFaultTodayByLevel = () => [
  { name: 'A', value: 0 },
  { name: 'B', value: 0 },
  { name: 'C', value: 4 },
  { name: '未分级', value: 26 },
];

// 2.10.1 站点 tab：以站点为维度进行今日告警统计（region 供省/市/区筛选命中）
export const getAlarmStationRows = () => [
  { key: 'hn', name: '海宁工业园站', a: 1, b: 3, c: 2, pending: 4, closed: 2 },
  { key: 'nx', name: '南湖充电站', a: 0, b: 2, c: 1, pending: 1, closed: 2 },
  { key: 'yx', name: '余新镇储能站', a: 0, b: 0, c: 1, pending: 0, closed: 1 },
  { key: 'xz', name: '秀洲经开区储能站', a: 0, b: 1, c: 1, pending: 1, closed: 1 },
].map((r) => {
  const g = STATION_REGIONS[r.key] || {};
  return { ...r, region: `${g.province || ''}${g.city || ''}${g.district || ''}` };
});

// 2.10.1 报警 tab：多站报警列表（列口径对齐 doc01 文档图：
//   站点名称 / 设备类型 / 设备名称 / 报警等级 / 报警标题 / 原始报警标题 /
//   报警发生时间 / 报警结束时间 / 状态；行内「转工单」）
export const getFaultAlarmList = () => [
  { key: 1, level: 'A', levelText: 'A 级（故障）', title: '电芯温度过高', origin: '电芯温度过高', station: '海宁工业园站', deviceType: 'BMS', device: '3#BMS电池堆-1#电池簇', value: '58 ℃', status: '发生中', time: '2026-10-17 12:34:03', end: '-' },
  { key: 2, level: 'A', levelText: 'A 级（故障）', title: '直流侧绝缘阻抗低', origin: '直流侧绝缘阻抗低', station: '海宁工业园站', deviceType: 'PCS', device: 'PCS-03', value: '220 kΩ', status: '发生中', time: '2026-10-17 09:15:03', end: '-' },
  { key: 3, level: 'B', levelText: 'B 级（告警）', title: '单体压差偏大', origin: '单体压差一级告警', station: '海宁工业园站', deviceType: 'BMS', device: '3#BMS电池堆-1#电池簇', value: '68 mV', status: '发生中', time: '2026-10-16 08:51:26', end: '-' },
  { key: 4, level: 'B', levelText: 'B 级（告警）', title: '空调通信中断', origin: '空调通信状态告警', station: '南湖充电站', deviceType: '空调', device: 'AC-03', value: '-', status: '发生中', time: '2026-10-16 08:36:47', end: '-' },
  { key: 5, level: 'C', levelText: 'C 级（预警）', title: 'SOC 低于设定下限', origin: 'SOC 越限告警', station: '南湖充电站', deviceType: 'BMS', device: '2#BMS电池堆-1#电池簇', value: '11 %', status: '已结束', time: '2026-10-15 21:04:33', end: '2026-10-16 06:12:00' },
  { key: 6, level: 'C', levelText: 'C 级（预警）', title: '光伏限功率运行', origin: '光伏限功率预警', station: '余新镇储能站', deviceType: 'PCS', device: 'PV-02', value: '限 80 %', status: '已结束', time: '2026-10-15 13:22:09', end: '2026-10-15 17:40:21' },
  { key: 7, level: '未分级', levelText: '未分级', title: '液冷出水温度偏高', origin: '液冷出水温度告警', station: '余新镇储能站', deviceType: '空调', device: 'LC-01', value: '32 ℃', status: '已结束', time: '2026-10-14 15:41:52', end: '2026-10-14 19:02:10' },
  { key: 8, level: '未分级', levelText: '未分级', title: '用户电表通讯异常', origin: '用户电表通讯告警', station: '秀洲经开区储能站', deviceType: '双向电表', device: '并网点电表 1', value: '-', status: '发生中', time: '2026-10-14 10:12:40', end: '-' },
  { key: 9, level: 'B', levelText: 'B 级（告警）', title: 'BMS 通信异常', origin: '三级BMS通讯异常', station: '海宁工业园站', deviceType: 'BMS', device: '3#BMS电池堆-1#电池簇', value: '-', status: '发生中', time: '2026-10-13 05:20:11', end: '-' },
  { key: 10, level: 'C', levelText: 'C 级（预警）', title: '电池簇温差偏大', origin: '电池簇温差预警', station: '秀洲经开区储能站', deviceType: 'BMS', device: '1#BMS电池堆-2#电池簇', value: '6.8 ℃', status: '已结束', time: '2026-10-12 03:15:00', end: '2026-10-12 09:48:32' },
];

// 报警 tab 设备类型（对齐文档）
export const ALARM_DEVICE_TYPES = ['站控设备', 'PCS', 'BMS', '双向电表', '空调', '摄像头'];

// 2.10.2 报警统计（doc01 文档图）：按 4 个模块分区 —— 状态 / 告警等级 / 设备类型 / 历史故障时长，
// 每分区含两张图：左侧「月份视图」（自带月份选择器）、右侧「年份视图」（自带年份选择器）。
const statHourSeries = (seed) => Array.from({ length: 24 }, (_, i) => ({
  label: `${String(i).padStart(2, '0')}:00`,
  hours: +((1.4 + ((i * 7 + seed) % 11) * 0.85)).toFixed(1),
}));

// 报警统计（2.10.2）基础演示数据
const STAT_BASE = {
  state: {
    month: [{ name: '发生中', value: 6 }, { name: '已结束', value: 7250 }],
    year: [{ name: '发生中', value: 8 }, { name: '已结束', value: 38666 }],
  },
  level: {
    month: [{ name: 'A', value: 0 }, { name: 'B', value: 8 }, { name: 'C', value: 190 }, { name: '未分级', value: 7158 }],
    year: [{ name: 'A', value: 368 }, { name: 'B', value: 866 }, { name: 'C', value: 300 }, { name: '未分级', value: 7918 }],
  },
  device: {
    month: [
      { name: '站控设备', value: 406 }, { name: 'PCS', value: 2 }, { name: 'BMS', value: 6290 },
      { name: '双向电表', value: 0 }, { name: '空调', value: 558 }, { name: '摄像头', value: 0 },
    ],
    year: [
      { name: '站控设备', value: 450 }, { name: 'PCS', value: 272 }, { name: 'BMS', value: 6650 },
      { name: '双向电表', value: 0 }, { name: '空调', value: 1300 }, { name: '摄像头', value: 0 },
    ],
  },
  hour: { month: statHourSeries(3), year: statHourSeries(7) },
};

// 命中所选省 / 市 / 区的站点数量（用于演示数据按地区分摊）
const statMatchedStations = (region) => {
  const list = Object.values(STATION_REGIONS);
  if (!region) return list.length;
  const { province = 'all', city = 'all', district = 'all' } = region;
  return list.filter((r) => (province === 'all' || r.province === province)
    && (city === 'all' || r.city === city)
    && (district === 'all' || r.district === district)).length;
};

// doc01 2.10.2 p43：报警统计查询区「根据省/市/区进行筛选查询」——此处按所选地区命中的站点占比分摊各统计值，
// 使地点筛选真实生效（演示数据无地区维度明细，故采用地区分摊）。
export const getAlarmStatSections = (region) => {
  const total = Object.keys(STATION_REGIONS).length || 1;
  const f = statMatchedStations(region) / total;
  const scaleList = (list) => (f >= 1 ? list : list.map((d) => ({ ...d, value: Math.round(d.value * f) })));
  const s = STAT_BASE;
  return {
    state: { month: scaleList(s.state.month), year: scaleList(s.state.year) },
    level: { month: scaleList(s.level.month), year: scaleList(s.level.year) },
    device: { month: scaleList(s.device.month), year: scaleList(s.device.year) },
    hour: { month: scaleList(s.hour.month), year: scaleList(s.hour.year) },
  };
};

// 报警统计月 / 年选择器默认值（演示）
export const ALARM_STAT_MONTH = '2022-10';
export const ALARM_STAT_YEAR = '2022';

// 2.10.3 通知管理：列表（报警类型 / 报警频率 / 通知方式 / 每日通知上限 / 通知接收人 / 上次修改时间）
export const NOTIFY_ROWS = [
  { key: 1, type: '故障(A)', freq: '中频', ways: 'App', limit: 20, receivers: '郑宁宁、admin', updated: '2026-10-20 13:43:24' },
  { key: 2, type: '报警(B)', freq: '高频', ways: 'App、短信', limit: 50, receivers: '运维一组', updated: '2026-10-05 16:42:10' },
  { key: 3, type: '预警(C)', freq: '低频', ways: 'App、邮件', limit: 10, receivers: 'om@gsecloud.cn', updated: '2026-09-28 09:05:33' },
  { key: 4, type: '电池预警', freq: '低频', ways: 'App、短信', limit: 10, receivers: '张伟', updated: '2026-09-20 14:30:08' },
];

// 2.10.3 通知管理：配置项（报警等级为单选，对齐 doc01 文档图）
export const NOTIFY_LEVEL_OPTIONS = ['故障(A)', '报警(B)', '预警(C)', '未分级', '电池预警'];
export const NOTIFY_FREQ_OPTIONS = [
  { value: '高频', desc: '报警后立即发出通知' },
  { value: '中频', desc: '持续报警 15 分钟后发出通知' },
  { value: '低频', desc: '持续报警 60 分钟后发出通知' },
];
export const NOTIFY_WAY_OPTIONS = [
  { value: 'App', label: 'App' },
  { value: '短信', label: '短信（每月限 500 条）' },
];
export const NOTIFY_LIMIT_OPTIONS = [10, 20, 50];
export const NOTIFY_RECEIVERS = ['张伟', '李娜', '王强', '运维一组', '运维二组', '郑宁宁', 'admin', 'om@gsecloud.cn'];

// 2.11 电池预警：列表（站点名称 / 装机功率·容量 / 投运日期 / 电站地址 / 最新诊断时间 / 预警状态）
export const getBatteryWarnRows = () => [
  { key: 'hn', name: '海宁工业园站', power: '1.25 MW / 2.50 MWh', onlineAt: '2024-06-18', address: '浙江省嘉兴市海宁市经济开发区', diagAt: '2026-10-12 02:15', state: '异常' },
  { key: 'nx', name: '南湖充电站', power: '0.80 MW / 1.60 MWh', onlineAt: '2025-03-09', address: '浙江省嘉兴市南湖区科技城', diagAt: '2026-10-12 02:15', state: '正常' },
  { key: 'yx', name: '余新镇储能站', power: '0.50 MW / 1.00 MWh', onlineAt: '2023-11-02', address: '浙江省嘉兴市南湖区余新镇', diagAt: '2026-10-12 02:15', state: '关注' },
  { key: 'xz', name: '秀洲经开区储能站', power: '0.90 MW / 1.80 MWh', onlineAt: '2024-04-15', address: '浙江省嘉兴市秀洲区经济技术开发区', diagAt: '2026-10-12 02:15', state: '正常' },
];

// 2.11 电池预警：诊断结论六个维度（对齐 doc01 文档图 p46）
export const BATTERY_WARN_TYPES = [
  { key: 'socHigh', label: 'SOC—致性异常-偏高' },
  { key: 'socLow', label: 'SOC—致性异常-偏低' },
  { key: 'microShort', label: '微短路' },
  { key: 'lowCapacity', label: '电池容量不足' },
  { key: 'badConn', label: '电连接异常' },
  { key: 'bmsCollect', label: 'BMS采集故障' },
];

// 诊断明细：原因分析 / 运维建议（各维度口径对齐文档）
export const BATTERY_WARN_REASON = {
  socHigh: '该电芯SOC与该簇其他电芯相比偏高',
  socLow: '该电芯SOC与该簇其他电芯相比偏低',
  microShort: '电芯内部存在微短路，或自放电过大',
  lowCapacity: '电芯低容，即使补电，容量也低于其他电芯',
  badConn: '该电芯电连接不良（如虚焊、螺丝松动）',
  bmsCollect: '采集线松动、BMU故障',
};
export const BATTERY_WARN_ADVICE = {
  socHigh: '建议垫放电至截止后，采用放电仪对该电芯进行放电',
  socLow: '建议垫放电至截止后，采用放电仪对该电芯进行补电',
  microShort: '建议返厂，及时更换',
  lowCapacity: '建议返厂，及时更换',
  badConn: '紧固螺丝，若无则建议返厂',
  bmsCollect: '重插并固定采集线；更换BMU',
};

// 诊断结论六个维度的状态（演示）：异常站点标记对应异常项，关注站点标记为「关注」，其余「正常」
const conclusionStates = (state) => {
  const out = BATTERY_WARN_TYPES.reduce((acc, t) => { acc[t.key] = '正常'; return acc; }, {});
  if (state === '异常') out.microShort = '异常';
  if (state === '关注') out.socHigh = '关注';
  return out;
};

// 2.11 电池预警：单个 BMS 单体的温度 / 电压曲线（文档口径：点击诊断明细中的单体展示）
export const getBatteryCurves = (seed = 0) => {
  const at = (i) => `${String(i).padStart(2, '0')}:00`;
  return {
    temp: Array.from({ length: 24 }, (_, i) => ({ t: at(i), temp: +(25.5 + Math.sin((i + seed) / 3.1) * 2.4 + (i % 5) * 0.35).toFixed(1) })),
    volt: Array.from({ length: 24 }, (_, i) => ({ t: at(i), volt: +(3.28 + Math.sin((i + seed) / 2.4) * 0.022 + (i % 4) * 0.004).toFixed(3) })),
  };
};

// 2.11 电池预警：站点电池诊断详情（信息行 + 诊断结论 + 诊断明细；口径对齐 doc01 文档图 p46）
export const getBatteryWarnDetail = (id = 'hn') => {
  const rows = getBatteryWarnRows();
  const row = rows.find((r) => r.key === id) || rows[0];
  const st = STATIONS.find((s) => s.id === id);
  return {
    conclusion: conclusionStates(row.state),
    station: row.name,
    power: row.power,
    onlineAt: row.onlineAt,
    address: row.address,
    diagAt: row.diagAt,
    state: row.state,
    installed: st ? `${st.rated.power} / ${st.rated.capacity}` : row.power,
    bmsRows: [
      { key: 'b1', name: '1#BMS', sub: '1#BMS堆' },
      { key: 'b2', name: '1#BMS', sub: '2#BMS堆' },
      { key: 'b3', name: '2#BMS', sub: '1#BMS堆' },
      { key: 'b4', name: '2#BMS', sub: '2#BMS堆' },
    ],
  };
};

// ============================== G 批次：分析中心 · 统计报表（doc01 2.12）==============================
// 2.12.1 电量报表 / 2.12.2 收益报表：多站（汇总）/ 单站切换，日报表 / 月报表切换，导出 Excel
// 电量单位 kWh，收益单位元；综合效率 = 总放电量 / 总充电量 * 100%

// 尖峰平谷基准电量（kWh，单日单站基准值）
const RPT_ENERGY_BASE = { sharp: 210, peak: 560, flat: 890, valley: 1260 };
// 买入 / 卖出分时电价（元/kWh，由用户在单站监控内配置）
const RPT_BUY = { sharp: 0.86, peak: 0.72, flat: 0.45, valley: 0.22 };
const RPT_SELL = { sharp: 1.28, peak: 1.05, flat: 0.65, valley: 0.32 };

// 电站并网点（单站报表按并网点拆分）
export const GRID_POINTS = ['并网点 1（1# 主变）', '并网点 2（2# 主变）', '并网点 3（光伏专线）'];

// 日报表 / 月报表时间维度
export const getReportPeriods = (mode = 'day') => (mode === 'day'
  ? ['10-06', '10-07', '10-08', '10-09', '10-10', '10-11']
  : ['04月', '05月', '06月', '07月', '08月', '09月']);

const rptScale = (i, pi = 0) => 1 + ((i * 7 + pi * 3) % 5) / 20 - pi * 0.08;

// 电量报表单行：各时段充放电量 / 时段合计（总）/ 日站用电量 / 综合效率
const energyRow = (key, label, scale) => {
  const row = { key, label };
  Object.keys(RPT_ENERGY_BASE).forEach((k) => {
    row[`${k}Charge`] = Math.round(RPT_ENERGY_BASE[k] * scale);
    row[`${k}Discharge`] = Math.round(RPT_ENERGY_BASE[k] * scale * 0.89);
  });
  const charge = row.sharpCharge + row.peakCharge + row.flatCharge + row.valleyCharge;
  const discharge = row.sharpDischarge + row.peakDischarge + row.flatDischarge + row.valleyDischarge;
  row.chargeTotal = charge;
  row.dischargeTotal = discharge;
  row.stationUse = Math.round(76 * scale);
  row.eff = +((discharge / charge) * 100).toFixed(1);
  return row;
};

// 收益报表单行：各时段充电成本 / 放电成本（时段合计）/ 日站用电成本 / 实际收益
const incomeRow = (key, label, scale) => {
  const row = { key, label };
  let cost = 0;
  let gain = 0;
  Object.keys(RPT_ENERGY_BASE).forEach((k) => {
    const c = Math.round(RPT_ENERGY_BASE[k] * scale);
    const d = Math.round(RPT_ENERGY_BASE[k] * scale * 0.89);
    row[`${k}Cost`] = Math.round(c * RPT_BUY[k]);
    row[`${k}Gain`] = Math.round(d * RPT_SELL[k]);
    cost += row[`${k}Cost`];
    gain += row[`${k}Gain`];
  });
  row.costTotal = cost;
  row.gainTotal = gain;
  row.stationCost = Math.round(76 * scale * RPT_SELL.flat);
  row.total = gain - cost;
  return row;
};

export const getEnergyReportMulti = (mode = 'day') => getReportPeriods(mode)
  .map((label, i) => energyRow(`em-${mode}-${i}`, label, rptScale(i)));

export const getEnergyReportSingle = (mode = 'day') => GRID_POINTS.flatMap((point, pi) =>
  getReportPeriods(mode).map((label, i) => ({ ...energyRow(`es-${mode}-${pi}-${i}`, label, rptScale(i, pi)), point })));

export const getIncomeReportMulti = (mode = 'day') => getReportPeriods(mode)
  .map((label, i) => incomeRow(`im-${mode}-${i}`, label, rptScale(i)));

export const getIncomeReportSingle = (mode = 'day') => GRID_POINTS.flatMap((point, pi) =>
  getReportPeriods(mode).map((label, i) => ({ ...incomeRow(`is-${mode}-${pi}-${i}`, label, rptScale(i, pi)), point })));

// ---------------- G 批次补充：汇总 · 站点报表（doc01 图 p47_0 / p48_0）----------------
// 按站点汇总一行 + 末尾「总计」行
const sumRows = (rows, keys, label = '总计') => {
  const out = { key: 'total', label };
  keys.forEach((k) => { out[k] = +rows.reduce((a, r) => a + (r[k] || 0), 0).toFixed(1); });
  return out;
};

const ENERGY_CHARGE_KEYS = ['sharpCharge', 'peakCharge', 'flatCharge', 'valleyCharge'];
const ENERGY_DISCHARGE_KEYS = ['sharpDischarge', 'peakDischarge', 'flatDischarge', 'valleyDischarge'];
const INCOME_COST_KEYS = ['sharpCost', 'peakCost', 'flatCost', 'valleyCost'];
const INCOME_GAIN_KEYS = ['sharpGain', 'peakGain', 'flatGain', 'valleyGain'];

// 电量报表 · 站点报表（站点维度一行；总计行含各时段合计、总充电量 / 总放电量、综合效率）
export const getEnergyStationReport = () => {
  const rows = STATIONS.map((s, i) => energyRow(`ets-${s.id}`, s.name, rptScale(i)));
  const total = sumRows(rows, [...ENERGY_CHARGE_KEYS, 'chargeTotal', ...ENERGY_DISCHARGE_KEYS, 'dischargeTotal', 'stationUse']);
  total.eff = +((total.dischargeTotal / total.chargeTotal) * 100).toFixed(1);
  return [...rows, total];
};

// 收益报表 · 站点报表（站点维度一行；总计行含各时段合计、总充电成本 / 总放电成本、站用电成本、实际收益）
export const getIncomeStationReport = () => {
  const rows = STATIONS.map((s, i) => incomeRow(`its-${s.id}`, s.name, rptScale(i)));
  const total = sumRows(rows, [...INCOME_COST_KEYS, 'costTotal', ...INCOME_GAIN_KEYS, 'gainTotal', 'stationCost']);
  total.total = +(total.gainTotal - total.costTotal).toFixed(1);
  return [...rows, total];
};

// ============================== G 批次：运维中心 · 计划 / 运维图谱 / 运维账号（doc01 2.16 ~ 2.18）==============================

// 2.16 计划：类型 / 周期枚举
export const OM_PLAN_KINDS = ['巡视检查', '维护保养'];
export const OM_PLAN_CYCLES = ['每日', '每周', '1个月', '3个月', '6个月', '12个月'];

export const OM_PLAN_ROWS = [
  { key: 1, kind: '巡视检查', name: '储能舱日常巡视', desc: '检查电池舱温湿度、消防与告警状态', firstAt: '2026-10-13 09:00', cycle: '每日', stations: '全部站点（6 个）', owner: '运维部 / 张伟', next: '2026-10-12 23:59:59', state: '执行中' },
  { key: 2, kind: '巡视检查', name: '并网点电表抄录核对', desc: '核对并网点电表抄表示数与平台电量数据', firstAt: '2026-10-14 08:30', cycle: '每周', stations: '海宁工业园站', owner: '运维部 / 李娜', next: '2026-10-13 23:59:59', state: '执行中' },
  { key: 3, kind: '维护保养', name: '季度电池均衡保养', desc: '对电池簇执行一次均衡与容量标定', firstAt: '2026-12-15 09:00', cycle: '3个月', stations: '全部站点（6 个）', owner: '工程部 / 王强', next: '2026-12-14 23:59:59', state: '待启动' },
  { key: 4, kind: '维护保养', name: '半年度变压器预防性试验', desc: '绝缘、耐压与温升试验', firstAt: '2027-04-01 09:00', cycle: '6个月', stations: '海宁工业园站、南湖充电站', owner: '工程部 / 周翔宇', next: '2027-03-31 23:59:59', state: '待启动' },
  { key: 5, kind: '巡视检查', name: '消防联动年度检测', desc: '烟感、水浸与消防主机联动测试', firstAt: '2026-11-02 10:00', cycle: '12个月', stations: '全部站点（6 个）', owner: '运维部 / 李文杰', next: '2026-11-01 23:59:59', state: '待启动' },
];

// 2.17 运维图谱：故障表现 → 后果影响 / 检查步骤 / 处理方法（运维故障专家库，各公司数据隔离）
// 列表列顺序（对齐 doc01 2.17 文档图）：故障表现 / 可能原因 / 后果影响 / 检查步骤 / 处理方法 / 操作
// 字段：symptom 故障表现 / cause 可能原因 / impact 后果影响 / steps 检查步骤 / solution 处理方法
//      signalKeys 关联遥信（多对多，仅取遥信，非必选可多选；来源总告警库不区分站点）
export const OM_GRAPH_ROWS = [
  { key: 1, symptom: 'BMS 过温', device: 'BMS 电池堆', signals: 'BMS 电堆过温遥信', impact: '可能导致 BMS 保护动作，储能系统停运', cause: '散热风道堵塞 / 空调失效', steps: '1) 查看堆温历史趋势；2) 检查空调运行与风道；3) 核对温度传感器采样', solution: '清理风道并重启空调，温度回落至 35 ℃ 以下后复归告警', author: '李文杰', updated: '2026-09-28 10:12', signalKeys: ['bms-ot'] },
  { key: 2, symptom: '直流侧绝缘阻抗低', device: 'PCS 变流器', signals: 'PCS 绝缘故障遥信', impact: '绝缘下降可能拉弧放电，危及人身与设备安全', cause: '直流侧线缆受潮或破损', steps: '1) 断电挂牌；2) 测量正负极对地绝缘；3) 排查线缆与接头', solution: '更换受损线缆并做绝缘处理，绝缘阻抗恢复 > 1 MΩ 后复归', author: '张伟', updated: '2026-09-20 16:40', signalKeys: ['pcs-ins'] },
  { key: 3, symptom: '单体压差偏大', device: 'BMS 电池簇', signals: '单体压差告警遥信', impact: '电池簇一致性恶化，可用容量与循环寿命下降', cause: '电芯一致性下降', steps: '1) 定位压差最大的单体；2) 分析压差趋势；3) 评估是否需均衡或更换', solution: '执行簇内均衡；若压差持续 > 100 mV，申请更换落后单体', author: '李娜', updated: '2026-09-12 09:05', signalKeys: ['bms-diff'] },
  { key: 4, symptom: '空调通信中断', device: '空调', signals: '空调通信状态遥信', impact: '电池舱温度失控，可能触发过温保护停机', cause: '通信线松动 / 模块掉电', steps: '1) 检查通信线与供电；2) 重启空调通信模块；3) 确认平台状态恢复', solution: '紧固通信线并重上电，平台通信状态恢复「正常」', author: '王强', updated: '2026-08-30 14:22', signalKeys: ['ac-comm'] },
];

// 2.17 关联规则：故障表现关联设备遥信（仅取遥信，非必选，可多选；来源为总告警库，不区分站点）
export const OM_GRAPH_SIGNAL_TREE = [
  {
    key: 'pcs', title: 'PCS 变流器', children: [
      { key: 'pcs-ins', title: '绝缘故障遥信' },
      { key: 'pcs-comm', title: '通信中断遥信' },
      { key: 'pcs-lock', title: '故障闭锁遥信' },
    ],
  },
  {
    key: 'bms', title: 'BMS 电池堆', children: [
      { key: 'bms-ot', title: '电堆过温遥信' },
      { key: 'bms-diff', title: '单体压差告警遥信' },
      { key: 'bms-soc', title: 'SOC 越限遥信' },
    ],
  },
  {
    key: 'ac', title: '空调', children: [
      { key: 'ac-comm', title: '通信状态遥信' },
      { key: 'ac-temp', title: '出水温度遥信' },
    ],
  },
  {
    key: 'fs', title: '消防', children: [
      { key: 'fs-smoke', title: '烟感遥信' },
      { key: 'fs-water', title: '水浸遥信' },
    ],
  },
];
export const OM_GRAPH_SIGNAL_KEYS = ['bms-ot', 'pcs-ins'];

// 2.18 运维账号：站点名称 / 运维人数 / 姓名（10 条分页）
export const OM_ACCOUNT_ROWS = [
  { key: 'hn', station: '海宁工业园站', count: 3, names: '张伟、李娜、王强' },
  { key: 'nx', station: '南湖充电站', count: 2, names: '李娜、周翔宇' },
  { key: 'yx', station: '余新镇储能站', count: 2, names: '王强、李文杰' },
  { key: 'xz', station: '秀洲经开区储能站', count: 2, names: '张伟、周翔宇' },
  { key: 'hy', station: '海盐智造园储能站', count: 1, names: '李文杰' },
  { key: 'ph', station: '平湖光储充站', count: 1, names: '李娜' },
];

// ============================== G 批次：运营中心 · 运营分析（doc01 2.21）==============================

// 多站报告列表（月份 + 省份唯一确定一条报告，重复发起会覆盖；省份为「-」代表全部省份）
export const OP_MULTI_REPORTS = [
  { key: 1, month: '2026-09', province: '浙江省', sites: 6, power: '4.75 MW', capacity: '9.50 MWh', state: '已生成', createdAt: '2026-10-01 02:30' },
  { key: 2, month: '2026-08', province: '-', sites: 6, power: '4.75 MW', capacity: '9.50 MWh', state: '已生成', createdAt: '2026-09-01 02:34' },
  { key: 3, month: '2026-07', province: '浙江省', sites: 5, power: '4.10 MW', capacity: '8.20 MWh', state: '已生成', createdAt: '2026-08-01 02:31' },
];

// 多站报告：总计区域 + 运营概况
export const getOpMultiSummary = () => ({
  company: '海宁绿能综合能源有限公司',
  month: '2026-09',
  period: '2026-09-01 ~ 2026-09-30',
  sites: 6,
  power: '4.75 MW',
  capacity: '9.50 MWh',
  income: 442200,          // 本月收益（元，各站储能实际收益加和）
  theoryRate: 78.2,        // 理论收益达成率（%）= 实际收益 / 多站理论收益加和
  unitIncome: 46.5,        // 单位容量收益（元/kWh）= 实际收益 / 总装机容量
  momRate: 12.8,           // 环比上月（%）
  chargeTheory: 198000,    // 月充电量 · 理论值（kWh）
  chargeActual: 186500,    // 月充电量 · 实际值（取自电量报表）
  dischargeTheory: 168300, // 月放电量 · 理论值
  dischargeActual: 157900, // 月放电量 · 实际值
  effTheory: 72.3,         // 综合效率 · 理论值 = (理论放电量 / 理论充电量) ^ 2
  effActual: 84.7,         // 综合效率 · 实际值
});

// 多站报告：各站明细 + 各站评价（达成率 ≥ 80% 优秀，< 60% 待提升，其余良好）
export const OP_STATION_DETAIL = [
  { key: 'hn', name: '海宁工业园站', grade: '优秀', days: 30, income: 128600, rate: 94.2, unitDay: 1.71, mom: 8.4 },
  { key: 'nx', name: '南湖充电站', grade: '优秀', days: 30, income: 96400, rate: 88.6, unitDay: 2.01, mom: 5.2 },
  { key: 'xz', name: '秀洲经开区储能站', grade: '优秀', days: 30, income: 88600, rate: 91.0, unitDay: 1.63, mom: 6.8 },
  { key: 'yx', name: '余新镇储能站', grade: '良好', days: 28, income: 42600, rate: 72.3, unitDay: 1.52, mom: -6.5 },
  { key: 'hy', name: '海盐智造园储能站', grade: '良好', days: 29, income: 54200, rate: 66.4, unitDay: 1.28, mom: 1.9 },
  { key: 'ph', name: '平湖光储充站', grade: '待提升', days: 26, income: 31800, rate: 48.7, unitDay: 0.92, mom: -12.3 },
];

export const OP_GRADE_PIE = [
  { name: '优秀', value: 3 },
  { name: '良好', value: 2 },
  { name: '待提升', value: 1 },
];

// 单站报告列表（每月 1 号自动生成，或发起多站报告时自动生成）
const OP_SITE_CAPACITY = {
  hn: { power: '1.25 MW', capacity: '2.50 MWh' },
  nx: { power: '0.80 MW', capacity: '1.60 MWh' },
  xz: { power: '0.85 MW', capacity: '1.70 MWh' },
  yx: { power: '0.50 MW', capacity: '1.00 MWh' },
  hy: { power: '0.70 MW', capacity: '1.40 MWh' },
  ph: { power: '0.65 MW', capacity: '1.30 MWh' },
};

export const OP_SINGLE_REPORTS = OP_STATION_DETAIL.map((s) => ({
  key: `${s.key}-09`,
  site: s.name,
  month: '2026-09',
  power: (OP_SITE_CAPACITY[s.key] || {}).power || '-',
  capacity: (OP_SITE_CAPACITY[s.key] || {}).capacity || '-',
  days: s.days,
  income: s.income,
  rate: s.rate,
  state: '已生成',
  createdAt: '2026-10-01 02:30',
}));

// 单站报告：总计区域 + 运营概况（理论收益 = (0.85 * 最高电价 - √0.85 * 最低电价) * 2 * 装机容量 * 运行天数）
export const OP_SINGLE_PARAMS = {
  hn: { site: '海宁工业园站', power: '1.25 MW', capacity: '2.50 MWh', capacityKwh: 2500, onlineAt: '2024-06-18', days: 30, income: 128600, theory: 136500, rate: 94.2, unitDay: 1.71, mom: 8.4 },
  nx: { site: '南湖充电站', power: '0.80 MW', capacity: '1.60 MWh', capacityKwh: 1600, onlineAt: '2025-03-09', days: 30, income: 96400, theory: 108800, rate: 88.6, unitDay: 2.01, mom: 5.2 },
  yx: { site: '余新镇储能站', power: '0.50 MW', capacity: '1.00 MWh', capacityKwh: 1000, onlineAt: '2023-11-02', days: 28, income: 42600, theory: 58900, rate: 72.3, unitDay: 1.52, mom: -6.5 },
  xz: { site: '秀洲经开区储能站', power: '0.90 MW', capacity: '1.80 MWh', capacityKwh: 1800, onlineAt: '2024-04-15', days: 30, income: 88600, theory: 97300, rate: 91.0, unitDay: 1.63, mom: 6.8 },
};

export const getOpSingleSummary = (id = 'hn') => {
  const p = OP_SINGLE_PARAMS[id] || OP_SINGLE_PARAMS.hn;
  return {
    ...p,
    month: '2026-09',
    chargeTheory: Math.round(p.capacityKwh * Math.sqrt(0.85) * p.days * 2),
    dischargeTheory: Math.round(p.capacityKwh * 0.85 * p.days * 2),
    effTheory: 72.3,
    effActual: 84.7,
  };
};

// 单站报告：本月明细（每日收益与充放电量）
export const getOpSingleMonth = () => Array.from({ length: 30 }, (_, i) => ({
  key: `09-${String(i + 1).padStart(2, '0')}`,
  label: `09-${String(i + 1).padStart(2, '0')}`,
  income: 4100 + ((i * 17) % 13) * 25,
  charge: 4400 + ((i * 11) % 11) * 30,
  discharge: 3900 + ((i * 7) % 9) * 35,
}));

// 单站报告：收益趋势（近 12 个月 单位容量日收益 与 理论收益达成率）
export const getOpSingleTrend = () => Array.from({ length: 12 }, (_, i) => ({
  key: `${2025 + Math.floor((9 + i) / 12)}-${String(((9 + i) % 12) + 1).padStart(2, '0')}`,
  month: `${String(((9 + i) % 12) + 1).padStart(2, '0')}月`,
  unitDay: +(1.02 + i * 0.058).toFixed(2),
  rate: +Math.min(68 + i * 2.4 + (i % 3) * 1.2, 94.2).toFixed(1),
}));

// ============================== H 批次：管理中心 · 组织成员 / 角色 / 外部公司 / 日志 / 个性配置（doc01 3.2 ~ 3.6）==============================

// 3.2 组织成员 —— 部门树：系统默认「所有成员」「默认部门」内置不可改删；其余部门可新建/改名/删除，部门内存在账号则不可删除
export const H_DEPT_TREE = [
  { key: 'd-all', title: '所有成员（6）', builtin: true },
  { key: 'd-default', title: '默认部门（2）', builtin: true },
  { key: 'd-ops', title: '运维部（2）', builtin: false },
  { key: 'd-biz', title: '运营部（1）', builtin: false },
  { key: 'd-tech', title: '技术部（1）', builtin: false },
  { key: 'd-market', title: '市场部（0）', builtin: false },
];

// 3.2 组织成员 —— 账号列表：账号名 / 姓名 / 角色 / 手机号 / 部门 / 状态（管理员 admin 属默认部门并置顶）
export const H_MEMBERS = [
  { key: 1, account: 'admin', name: '管理员', role: '管理员', phone: '138****4270', dept: '默认部门', state: '启用', top: true },
  { key: 2, account: 'zzhouxy', name: '周翔宇', role: '管理员', phone: '138****4270', dept: '技术部', state: '启用' },
  { key: 3, account: 'liwj', name: '李文杰', role: '现场运维', phone: '139****8812', dept: '运维部', state: '启用' },
  { key: 4, account: 'chensy', name: '陈思远', role: '平台技术支持', phone: '137****2205', dept: '运营部', state: '启用' },
  { key: 5, account: 'wangmq', name: '王梦琪', role: '运营专员', phone: '135****9931', dept: '运维部', state: '禁用' },
  { key: 6, account: 'zhaoym', name: '赵一鸣', role: '只读访客', phone: '186****4477', dept: '默认部门', state: '启用' },
];

// 3.2 添加成员 —— 站点分配方式（单选，默认省市区）
export const H_ASSIGN_MODES = ['省市区', '站点标签', '外部公司', '手动选择'];

// 3.3 角色管理 —— 系统内置 3 种角色 + 其他自定义角色（权限为主管理员子集）
export const H_ROLES = [
  { key: 1, name: '管理员', builtin: true, creator: '系统内置', created: '2024-01-01', members: 2, desc: '内置模板，由方舟控制菜单权限；不可编辑和删除' },
  { key: 2, name: '平台技术支持', builtin: true, creator: '系统内置', created: '2024-01-01', members: 1, desc: '内置模板，可编辑菜单权限，不可删除；轻舟官方可通过该账号访问本公司平台' },
  { key: 3, name: '现场运维', builtin: true, creator: '系统内置', created: '2024-01-01', members: 1, desc: '内置模板，可编辑菜单权限，不可删除；仅该角色可操作 APP 工单接单' },
  { key: 4, name: '运营专员', builtin: false, creator: '周翔宇', created: '2026-03-18', members: 1, desc: '主管理员权限的子集：运营分析与报表导出' },
  { key: 5, name: '只读访客', builtin: false, creator: '周翔宇', created: '2026-05-06', members: 1, desc: '主管理员权限的子集：仅查看，不可操作' },
];

// 3.4 外部公司 —— 公司名称 / 社会信用代码 / 关联创建时间 / 联系人 / 联系电话 / 已授权电站 / 权限
export const H_COMPANIES = [
  { key: 1, name: '嘉兴吉晟电力', credit: '91330481MA2C****3A', linkedAt: '2026-02-11', contact: '周翔宇', phone: '138****4270', stations: 3, perms: '查看 / 修改 / 控制', state: '合作中' },
  { key: 2, name: '杭州储能科技', credit: '91330106MA2H****7K', linkedAt: '2026-03-20', contact: '李文杰', phone: '139****8812', stations: 1, perms: '查看', state: '合作中' },
  { key: 3, name: '浙江新能源集团', credit: '91330000MA2J****9M', linkedAt: '2026-08-02', contact: '赵一鸣', phone: '186****4477', stations: 0, perms: '—', state: '洽谈中' },
];

// 3.4 权限分配 —— 电站权限三级（默认查看）
export const H_COMPANY_PERMS = [
  { key: 'view', name: '查看', desc: '电站数据可读' },
  { key: 'edit', name: '修改', desc: '电站信息可修改' },
  { key: 'ctrl', name: '控制', desc: '电站设备、策略可控' },
];

// 3.5 日志管理 —— 时间 / 操作内容 / 账号名称（默认近 7 日，按操作时间由近到远排序）
export const H_LOGS = [
  { key: 1, time: '2026-10-11 09:42:11', content: '下发预置策略至「海宁工业园站」', account: '周翔宇' },
  { key: 2, time: '2026-10-11 09:16:03', content: '确认告警「PCS-03 直流侧绝缘偏低」', account: '李文杰' },
  { key: 3, time: '2026-10-11 08:51:26', content: '登录平台', account: '陈思远' },
  { key: 4, time: '2026-10-11 08:36:47', content: '修改站点电价方案「余新镇储能站」', account: '周翔宇' },
  { key: 5, time: '2026-10-10 17:22:40', content: '新增组织成员「王梦琪」', account: '周翔宇' },
  { key: 6, time: '2026-10-10 15:08:12', content: '导出电量报表（近 7 日）', account: '陈思远' },
  { key: 7, time: '2026-10-09 10:44:55', content: '解除外部公司关联「绍兴绿能科技」', account: '周翔宇' },
];

// 3.6 个性配置 —— 上传项规格（格式 / 尺寸 / 应用位置）
export const H_THEME_ITEMS = [
  { key: 'loginLogo', label: '登录页 logo', format: 'png', size: '128px × 400px', usage: '应用于登录页和系统正常模式下的导航栏', value: 'login-logo.png（已上传）' },
  { key: 'navLogo', label: '导航栏 logo', format: 'png', size: '128px × 128px', usage: '应用于系统收缩模式下的导航栏', value: 'nav-logo.png（已上传）' },
  { key: 'favicon', label: '网页 icon', format: 'ico', size: '64px × 64px', usage: '应用于网页图标', value: 'favicon.ico（已上传）' },
];

// 3.7 客户户号管理 —— 客户简称 / 用电户号 / 用电类别 / 关联站点个数 / 新增时间（默认 10 行分页，按新增时间降序）
export const H_CUSTOMERS = [
  { key: 1, short: '海宁工业园', full: '海宁工业园储能科技有限公司', no: '3304****2210', category: '大工业用电', stations: 2, nodes: ['海宁工业园站', '余新镇储能站'], createdAt: '2026-09-28', region: '浙江省嘉兴市', location: '海宁市经济开发区', contact: '周翔宇', phone: '138****4270' },
  { key: 2, short: '余新镇储能', full: '余新镇新能源有限公司', no: '3304****3325', category: '一般工商业', stations: 1, nodes: ['余新镇储能站'], createdAt: '2026-09-20', region: '浙江省嘉兴市', location: '南湖区余新镇', contact: '陈思远', phone: '137****2205' },
  { key: 3, short: '南湖充电站', full: '南湖智慧充电服务有限公司', no: '3304****4471', category: '一般工商业', stations: 1, nodes: ['南湖充电站'], createdAt: '2026-08-15', region: '浙江省嘉兴市', location: '南湖区', contact: '李文杰', phone: '139****8812' },
  { key: 4, short: '临平储能', full: '杭州临平储能科技有限公司', no: '3301****8890', category: '大工业用电', stations: 1, nodes: ['杭州临平储能站'], createdAt: '2026-07-30', region: '浙江省杭州市', location: '临平区', contact: '赵一鸣', phone: '186****4477' },
  { key: 5, short: '柯桥微网', full: '绍兴柯桥微网能源有限公司', no: '3306****1123', category: '一般工商业', stations: 1, nodes: ['绍兴柯桥微网站'], createdAt: '2026-06-12', region: '浙江省绍兴市', location: '柯桥区', contact: '赵一鸣', phone: '186****4477' },
];

// 3.7 用电类别枚举
export const H_CUSTOMER_CATEGORIES = ['大工业用电', '一般工商业', '农业生产用电', '居民生活用电'];

// ============================== H 批次延伸：云平台门户 / 设置中心（《云平台账号权限》用户手册）==============================

// 功能角色（门户「设置中心」）：内置角色以 * 标注；储能车 / 现场运维为服务型特殊角色
export const PORTAL_ROLES = [
  { key: 1, name: '管理员', builtin: true, service: false, members: 2, desc: '拥有最高权限，只可以查看详情和复制' },
  { key: 2, name: '储能车', builtin: true, service: true, members: 1, desc: '为储能管家服务的特殊角色，进入储能管家后直接进入单站；不可编辑、复制、删除' },
  { key: 3, name: '现场运维', builtin: true, service: true, members: 1, desc: '为储能管家服务的特殊角色，仅该角色可操作 APP 工单；可编辑菜单权限、复制，不可删除' },
  { key: 4, name: '运营专员', builtin: false, service: false, members: 1, desc: '自定义角色，权限为主管理员权限的子集' },
  { key: 5, name: '只读访客', builtin: false, service: false, members: 1, desc: '自定义角色，仅可查看，不可操作' },
];

// 待激活的邀请（门户「设置中心 - 组织成员」tab）
export const PORTAL_INVITES = [
  { key: 1, invitee: '王梦琪', dept: '运维部', role: '现场运维', created: '2026-10-10', expire: '2026-10-25', state: '待激活', link: 'https://ems.demo/invite/1' },
  { key: 2, invitee: '赵一鸣', dept: '市场部', role: '只读访客', created: '2026-10-06', expire: '2026-10-21', state: '待激活', link: 'https://ems.demo/invite/2' },
  { key: 3, invitee: '孙倩', dept: '运营部', role: '运营专员', created: '2026-09-28', expire: '2026-10-13', state: '已过期', link: 'https://ems.demo/invite/3' },
];

// 平台名称多语言（门户「个性配置」）：中文 / 英文必填，其他语种可选
export const PORTAL_PLATFORM_LANGS = [
  { key: 'zh', label: '中文', value: '智慧能源管理云平台', required: true },
  { key: 'en', label: '英文', value: 'Energy Management Cloud', required: true },
  { key: 'other', label: '其他语言', value: '—（可选，点击下拉切换小语种）', required: false },
];

// 产品列表（门户「设置中心 - 功能角色 - 权限配置」勾选产品）：云平台聚合的储能产品线
export const PORTAL_PRODUCTS = [
  { key: 'ems', name: '能源管家', desc: '储能电站监控、控制与运维（主产品）' },
  { key: 'jny', name: '聚能盈', desc: '工商业储能投资与收益运营' },
];

// 地区选项（添加成员 - 站点分配方式「省市区」）：选择某个国家/地区的站点分配给成员
export const PORTAL_REGIONS = [
  '中国 · 浙江省 · 嘉兴市',
  '中国 · 浙江省 · 杭州市',
  '中国 · 浙江省 · 绍兴市',
  '中国 · 江苏省 · 苏州市',
  '中国 · 安徽省 · 合肥市',
];

// 已加入的公司列表（门户「切换公司」悬停展示）
export const PORTAL_COMPANY_LIST = [
  { key: 'gse', name: '嘉兴吉晟电力', short: '吉晟电力', domain: 'gse.ener-os.com', state: '启用' },
  { key: 'hzcn', name: '杭州储能科技', short: '杭州储能', domain: 'hzcn.ener-os.com', state: '启用' },
  { key: 'zjne', name: '浙江新能源集团', short: '浙新能', domain: 'zjne.ener-os.com', state: '禁用' },
];

// 门户异常提醒文案（《云平台账号权限》手册）
export const PORTAL_NOTICES = [
  { key: 'no-product', text: '暂无产品使用权，请联系公司管理员。' },
  { key: 'no-menu', text: '您在该产品中暂无菜单权限，请联系管理员。' },
  { key: 'disabled', text: '账号被禁用，请联系管理员。' },
];

// ============================== J 批次：工单详情 / 核算统计（doc01 2.15 / 2.20）===

// 工单进度流程（工单详情二级页：进度流程 + 执行详情）
export const WO_PROGRESS = [
  { key: 1, node: '创建工单', operator: '系统', time: '2026-10-11 09:16', remark: '由报警「PCS-03 直流侧绝缘偏低」自动转单' },
  { key: 2, node: '接单', operator: '李文杰', time: '2026-10-11 09:20', remark: '现场运维接单' },
  { key: 3, node: '现场执行', operator: '李文杰', time: '2026-10-11 10:05', remark: '已到达现场，开始排查直流侧绝缘电阻' },
  { key: 4, node: '处理完成', operator: '—', time: '—', remark: '待填写处理结果与照片' },
  { key: 5, node: '关闭', operator: '—', time: '—', remark: '待完成' },
];

// 核算统计概览（核算管理 · 核算统计 tab）
export const SETTLE_STAT = {
  last12: 528340, confirmed: 116560, unconfirmed: 0,
  confirmRate: 75, monthCount: 4, unconfirmedCount: 1,
};

// 收益统计（按站点汇总）
export const SETTLE_BY_STATION = [
  { key: 1, station: '海宁工业园站', records: 2, confirmedAmount: 95080, unconfirmedAmount: 0, rate: '100%', latest: '2026-09' },
  { key: 2, station: '余新镇储能站', records: 1, confirmedAmount: 21480, unconfirmedAmount: 0, rate: '100%', latest: '2026-09' },
  { key: 3, station: '南湖充电站', records: 1, confirmedAmount: 0, unconfirmedAmount: 0, rate: '0%', latest: '2026-09' },
];